import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';

import StockRankTable, { StockRow } from '../components/stock/StockRankTable';
import MarketSeriesChart, { IndicatorState, MarketView } from '../components/stock/MarketSeriesChart';
import StockSearchSuggestInput from '../components/common/StockSearchSuggestInput';
import FinancialAnalyticsPanel from '../components/financial/FinancialAnalyticsPanel';
import type { StockSearchSuggestion } from '../api/stockSearchApi';
import {
  getProfessionalFinancialHistory,
  searchProfessionalScreener,
  type ProfessionalFinancialHistoryItem,
} from '../api/professionalScreenerApi';
import SEO from '../components/SEO';
import '../styles/policy-workbench.css';
import '../styles/policy-intelligence-ui.css';

import { PI_RUNTIME } from '../styles/policySignalRuntime';
type Tone = 'blue' | 'green' | 'amber' | 'red' | 'slate';
type SortKey = 'decision' | 'value' | 'volume' | 'change' | 'policy' | 'financial' | 'close';
type SortOrder = 'asc' | 'desc';
type StockMarketFilter = 'ALL' | 'KOSPI' | 'KOSDAQ' | 'ETF' | 'ETN' | 'KONEX' | 'OTHER';
type DecisionBucket = 'Action' | 'Review' | 'Wait' | 'Risk' | 'Low';
type StockDetailTab = 'overview' | 'chart' | 'financial' | 'issues';

type StockFinancialProfileResponse = {
  ticker?: string | null;
  corpCode?: string | null;
  corpName?: string | null;
  bsnsYear?: string | null;
  reprtCode?: string | null;
  fsDiv?: string | null;

  revenue?: number | null;
  operatingIncome?: number | null;
  netIncome?: number | null;
  totalAssets?: number | null;
  totalLiabilities?: number | null;
  totalEquity?: number | null;
  operatingCashFlow?: number | null;

  financialScore?: number | null;
  financial_score?: number | null;
  score?: number | null;
  financialGrade?: string | null;
  financial_grade?: string | null;
  financialRiskLevel?: string | null;
  financial_risk_level?: string | null;
  outlookLabel?: string | null;
  outlook_label?: string | null;
  riskLabel?: string | null;
  risk_label?: string | null;

  gptFinancialSummary?: string | null;
  gpt_financial_summary?: string | null;
  adjustedFinancialSummary?: string | null;
  adjusted_financial_summary?: string | null;

  operatingMargin?: number | null;
  netMargin?: number | null;
  debtRatio?: number | null;
  equityRatio?: number | null;

  sourceRceptNo?: string | null;
  updatedAt?: string | null;
  updated_at?: string | null;
};


type StockBusinessProfileResponse = {
  ticker?: string | null;
  corpName?: string | null;
  corp_name?: string | null;
  market?: string | null;

  reportDate?: string | null;
  report_date?: string | null;
  reportName?: string | null;
  report_name?: string | null;
  rceptNo?: string | null;
  rcept_no?: string | null;

  industryL1?: string | null;
  industry_l1?: string | null;
  industryL2?: string | null;
  industry_l2?: string | null;
  industryL3?: string | null;
  industry_l3?: string | null;

  primarySector?: string | null;
  primary_sector?: string | null;
  secondarySectors?: string[] | string | null;
  secondary_sectors?: string[] | string | null;
  classificationConfidence?: number | null;
  classification_confidence?: number | null;
  classificationReason?: string | null;
  classification_reason?: string | null;

  businessSummary?: string | null;
  business_summary?: string | null;
  businessReportSummary?: string | null;
  business_report_summary?: string | null;
  currentBusinessSummary?: string | null;
  current_business_summary?: string | null;

  productTerms?: string[] | string | null;
  product_terms?: string[] | string | null;
  technologyTerms?: string[] | string | null;
  technology_terms?: string[] | string | null;
  coreTerms?: string[] | string | null;
  core_terms?: string[] | string | null;

  updatedAt?: string | null;
  updated_at?: string | null;
};

type ExtendedStockRow = StockRow & {
  tradingValue?: number;
  tradingValueShare?: number;
  tradingValueRank?: number;
  liquidityTier?: 'A' | 'B' | 'C';

  rsLocalPct?: number;
  signalStrength?: number;
  hasSpikeLike?: boolean;

  priceStage?: string;
  flowStage?: string;
  crowdingStage?: string;
  actionLabel?: string;
  eventStage?: string;
  timingState?: string;

  foreignZ?: number;
  maGap20Pct?: number;
  maGap60Pct?: number;

  priorityScore?: number;
  contradictionScore?: number;
  confidenceScore?: number;
  decisionLabel?: string;
  trendLabel?: string;
  flowLabel?: string;

  policyCount7d?: number;
  directCount7d?: number;
  indirectCount7d?: number;
  maxArticleImpactScore?: number;
  avgArticleConfidence?: number;
  maxPolicyImpactScore?: number;
  hasDirectPolicy?: boolean;
  hasIndirectPolicy?: boolean;

  reportSector?: string;
  dailyIssueCount?: number;
  insightTitle?: string;
  insightSummary?: string;
  insightInterpretation?: string;
  insightRisk?: string;
  insightAction?: string;

  financialProfile?: StockFinancialProfileResponse | null;
  financialScore?: number;
  financialGrade?: string;
  financialRiskLevel?: string;
  financialOutlookLabel?: string;
  financialRiskLabel?: string;
  financialSummary?: string;
  financialUpdatedAt?: string;

  businessProfile?: StockBusinessProfileResponse | null;

  pbr?: number;
  per?: number;
  bps?: number;
  eps?: number;

  date?: string;
  open?: number;
  high?: number;
  low?: number;
  marketCap?: number;

  // Decision Chart levels are backend-authoritative when provided.
  judgmentMaintainPrice?: number;
  judgmentImprovePrice?: number;
  judgmentRiskPrice?: number;
  judgmentChangedCount?: number;
};

type ExtraIndicatorFlags = {
  vfi?: boolean;
  mfi?: boolean;
  volSMA5?: boolean;
  volSMA20?: boolean;
  volSpike?: boolean;
  buySellRatio?: boolean;
  lci?: boolean;
};

const UI = {
  bg: PI_RUNTIME.canvas,
  card: PI_RUNTIME.surface,
  cardSoft: PI_RUNTIME.surfaceResearch,
  border: PI_RUNTIME.borderQuiet,
  borderStrong: PI_RUNTIME.borderStrong,
  text: PI_RUNTIME.text,
  textSub: PI_RUNTIME.textSecondary,
  textMuted: PI_RUNTIME.textMuted,
  blue: PI_RUNTIME.blue,
  blueSoft: 'rgba(41,104,255,.075)',
  green: PI_RUNTIME.positive,
  greenSoft: PI_RUNTIME.positiveSoft,
  amber: PI_RUNTIME.watch,
  amberSoft: PI_RUNTIME.watchSoft,
  red: PI_RUNTIME.risk,
  redSoft: PI_RUNTIME.riskSoft,
  slate: PI_RUNTIME.neutral,
  slateSoft: PI_RUNTIME.neutralSoft,
};

const today = dayjs().format('YYYY-MM-DD');
const defaultFrom = dayjs().subtract(60, 'day').format('YYYY-MM-DD');

const QUICK_BUCKET_LIST_ENDPOINT = '/api/category/stocks/quick-bucket-list';
const QUICK_BUCKET_DETAIL_ENDPOINT = '/api/category/stocks/quick-bucket-detail';
const STOCK_SEARCH_ENDPOINT = '/api/stocks/search';

const PRELOAD_LIMIT = 5000;
const PAGE_SIZE = 100;


function piFinancialBindingLog(stage: string, fields: Record<string, unknown>) {
  if (typeof window === 'undefined' || !new URLSearchParams(window.location.search).has('piChartDebug')) return;
  // Do not log financial payloads, cookies, API headers or sensitive values.
  console.info('[PI_FINANCIAL_BINDING]', stage, fields);
}

// FINANCIAL_HISTORY_RESPONSE_CONTRACT_V3
// Axios interceptors and backend controllers may return {items}, {data:{items}}
// or {data:{data:{items}}}. The parent must pass the verified five rows to
// FinancialAnalyticsPanel; a successful HTTP response is not proof of binding.
function normalizeFinancialHistoryResponse(input: any, expectedTicker: string): {
  items: ProfessionalFinancialHistoryItem[];
  latestYear: string;
  basis: string;
  found: boolean;
  envelope: string;
} {
  const candidates: Array<{ value: any; envelope: string }> = [
    { value: input, envelope: 'root' },
    { value: input?.data, envelope: 'data' },
    { value: input?.data?.data, envelope: 'data.data' },
    { value: input?.result, envelope: 'result' },
    { value: input?.payload, envelope: 'payload' },
    { value: input?.response?.data, envelope: 'response.data' },
  ];
  const found = candidates.find(({ value }) => value && typeof value === 'object' &&
    (Array.isArray(value.items) || Array.isArray(value.rows) || Array.isArray(value.list) || value.found === false));
  if (!found) throw new Error('FINANCIAL_HISTORY_RESPONSE_SHAPE_MISMATCH');
  const body = found.value;
  const responseTicker = normalizeTickerInput(firstText(body.ticker, body.stockCode, body.stock_code));
  if (responseTicker && responseTicker !== expectedTicker) throw new Error('FINANCIAL_HISTORY_TICKER_MISMATCH');
  const records: any[] = Array.isArray(body.items) ? body.items : Array.isArray(body.rows) ? body.rows : Array.isArray(body.list) ? body.list : [];
  if (body.found === false && records.length) throw new Error('FINANCIAL_HISTORY_FOUND_CONTRADICTION');
  const byYear = new Map<string, any>();
  for (const record of records) {
    const ticker = normalizeTickerInput(firstText(record?.ticker, record?.stockCode, record?.stock_code));
    if (ticker && ticker !== expectedTicker) throw new Error('FINANCIAL_HISTORY_ROW_TICKER_MISMATCH');
    const year = String(record?.year ?? record?.bsnsYear ?? '').trim();
    if (!/^\d{4}$/.test(year)) continue;
    const numericFields = [
      'revenue','operatingIncome','netIncome','totalAssets','totalLiabilities','totalEquity',
      'operatingCashFlow','capex','cashAndEquivalents','totalBorrowings','netDebt','freeCashFlow',
      'ocfMarginPct','capexToRevenuePct','fcfMarginPct','ocfToNetIncome',
      'interestCoverageAnnual','netDebtToOperatingIncomeAnnual',
    ];
    const normalized: Record<string, any> = { ...record, year };
    for (const field of numericFields) {
      const value = record?.[field];
      normalized[field] = value == null || value === '' ? null : Number.isFinite(Number(value)) ? Number(value) : null;
    }
    // Prefer consolidated (CFS) when both CFS and OFS exist for one fiscal year.
    const prior = byYear.get(year);
    if (!prior || (String(prior.fsDiv).toUpperCase() !== 'CFS' && String(normalized.fsDiv).toUpperCase() === 'CFS')) {
      byYear.set(year, normalized);
    }
  }
  const items = Array.from(byYear.values()).sort((a, b) => Number(a.year) - Number(b.year)) as ProfessionalFinancialHistoryItem[];
  const latestYear = String(body.latestYear ?? items[items.length - 1]?.year ?? '');
  return { items, latestYear, basis: String(body.basis ?? ''), found: body.found !== false, envelope: found.envelope };
}

const QUICK_BUCKET_CACHE_TTL_MS = 60_000;
let quickBucketListCache: { key: string; savedAt: number; rows: ExtendedStockRow[] } | null = null;


// Opt-in diagnostic tracing only. Enable with ?piChartDebug=1 on /stocks.
// Only public ticker, selected session dates, timings, statuses and numeric
// coverage indicators are kept. No credentials, response bodies or search text.
const PI_CHART_TRACE_PAGE_RUN = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
let piChartTraceSequence = 0;
const PI_CHART_TRACE_KEY = 'PI_CHART_DIAG_EVENTS';

type PiChartTraceFields = Record<string, string | number | boolean | null | undefined>;

function piChartTracingEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('piChartDebug') === '1'
      || window.sessionStorage.getItem('PI_CHART_DEBUG') === '1';
  } catch { return false; }
}

function piChartTrace(event: string, fields: PiChartTraceFields = {}): void {
  if (!piChartTracingEnabled()) return;
  try {
    const record = {
      timestamp: new Date().toISOString(),
      pageRun: PI_CHART_TRACE_PAGE_RUN,
      seq: ++piChartTraceSequence,
      event,
      ...fields,
    };
    const previous = JSON.parse(window.sessionStorage.getItem(PI_CHART_TRACE_KEY) || '[]');
    const next = Array.isArray(previous) ? previous.slice(-299) : [];
    next.push(record);
    window.sessionStorage.setItem(PI_CHART_TRACE_KEY, JSON.stringify(next));
    console.info('[PI_CHART_DIAG]', record);
  } catch {
    // Diagnostics must never alter production rendering or request behavior.
  }
}

function piChartElapsed(start: number): number {
  return Math.round((performance.now() - start) * 10) / 10;
}

function piChartPublicPath(rawUrl: string): string {
  // Intentionally strip the full query string and never log API response bodies.
  return rawUrl.split('?')[0];
}

const S = (value: any) => (typeof value === 'string' ? value : value == null ? '' : String(value));

function n(value: any): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (value == null || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function norm(value: any) {
  return S(value).trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
}

function contains(a: any, b: any) {
  return S(a).toLowerCase().includes(S(b).toLowerCase());
}


function normalizeTickerInput(value: string) {
  const compact = S(value)
    .normalize('NFKC')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '');

  if (/^A\d{6}$/.test(compact)) return compact.slice(1);
  return compact;
}

function looksLikeTicker(value: string) {
  return /^\d{6}$/.test(normalizeTickerInput(value));
}

function normalizeStockSearchText(value: string) {
  return S(value)
    .normalize('NFKC')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function stockNameKey(value: string) {
  return normalizeStockSearchText(value)
    .replace(/^(?:\(주\)|㈜|주식회사)\s*/u, '')
    .replace(/\s+/g, '')
    .toLocaleLowerCase('ko-KR');
}

async function fetchJSON<T = any>(
  url: string,
  signal?: AbortSignal,
  cache: RequestCache = 'default',
): Promise<T> {
  const requestId = ++piChartTraceSequence;
  const started = performance.now();
  const endpoint = piChartPublicPath(url);
  piChartTrace('http:start', { requestId, endpoint, cache });
  try {
    const response = await fetch(url, {
      credentials: 'include',
      headers: { Accept: 'application/json' },
      signal,
      cache,
    });
    piChartTrace('http:response', {
      requestId, endpoint, status: response.status,
      ok: response.ok, elapsedMs: piChartElapsed(started),
      contentType: response.headers.get('content-type')?.split(';')[0] || '',
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const parsed = await response.json();
    piChartTrace('http:parsed', {
      requestId, endpoint, elapsedMs: piChartElapsed(started),
      responseKind: Array.isArray(parsed) ? 'array' : typeof parsed,
    });
    return parsed as T;
  } catch (error: any) {
    piChartTrace('http:error', {
      requestId, endpoint, elapsedMs: piChartElapsed(started),
      errorName: String(error?.name || 'Error'),
      errorKind: signal?.aborted ? 'ABORTED' : /HTTP \d+/.test(String(error?.message)) ? 'HTTP_FAILURE' : 'NETWORK_OR_PARSE',
    });
    throw error;
  }
}

async function resolveStockSuggestions(query: string, limit = 10): Promise<StockSearchSuggestion[]> {
  const clean = normalizeStockSearchText(query);
  if (!clean) return [];

  const params = new URLSearchParams();
  params.set('q', clean);
  params.set('limit', String(Math.max(1, Math.min(limit, 30))));

  const raw = await fetchJSON<any>(`${STOCK_SEARCH_ENDPOINT}?${params.toString()}`);
  const list = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.items)
      ? raw.items
      : Array.isArray(raw?.data)
        ? raw.data
        : [];

  return list
    .map((item: any) => {
      const ticker = normalizeTickerInput(
        S(item?.ticker ?? item?.code ?? item?.stockCode ?? item?.stock_code),
      );
      const corpName = normalizeStockSearchText(
        S(item?.corpName ?? item?.corp_name ?? item?.name),
      );
      if (!/^\d{6}$/.test(ticker) || !corpName) return null;
      return {
        ...item,
        ticker,
        corpName,
      } as StockSearchSuggestion;
    })
    .filter(Boolean) as StockSearchSuggestion[];
}

function setSearchParam(key: string, value?: string) {
  try {
    const url = new URL(window.location.href);

    if (value == null || value === '') {
      url.searchParams.delete(key);
    } else {
      url.searchParams.set(key, value);
    }

    window.history.replaceState(window.history.state, '', url.toString());
  } catch {}
}

function useQueryState<T>(
  key: string,
  defaultValue: T,
  parse: (value: string) => T,
  format: (value: T) => string,
) {
  const [state, setState] = useState<T>(() => {
    try {
      const url = new URL(window.location.href);
      const value = url.searchParams.get(key);
      return value != null ? parse(value) : defaultValue;
    } catch {
      return defaultValue;
    }
  });
  const formatRef = useRef(format);
  formatRef.current = format;

  useEffect(() => {
    try {
      const url = new URL(window.location.href);

      if (state == null || (typeof state === 'string' && state === '')) {
        url.searchParams.delete(key);
      } else {
        url.searchParams.set(key, formatRef.current(state));
      }

      window.history.replaceState(window.history.state, '', url.toString());
    } catch {}
  }, [key, state]);

  return [state, setState] as const;
}

function useIsMobile(breakpoint = 820) {
  const getValue = useCallback(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= breakpoint;
  }, [breakpoint]);

  const [isMobile, setIsMobile] = useState(getValue);

  useEffect(() => {
    const onResize = () => setIsMobile(getValue());

    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, [getValue]);

  return isMobile;
}

function toneColor(tone: Tone) {
  if (tone === 'green') return UI.green;
  if (tone === 'amber') return UI.amber;
  if (tone === 'red') return UI.red;
  if (tone === 'blue') return UI.blue;
  return UI.slate;
}

function toneBg(tone: Tone) {
  if (tone === 'green') return UI.greenSoft;
  if (tone === 'amber') return UI.amberSoft;
  if (tone === 'red') return UI.redSoft;
  if (tone === 'blue') return UI.blueSoft;
  return UI.slateSoft;
}

function formatNumber(value?: number | null) {
  if (value == null || !Number.isFinite(Number(value))) return '-';
  return new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 0 }).format(Number(value));
}

function formatPct(value?: number | null) {
  if (value == null || !Number.isFinite(Number(value))) return '-';
  const parsed = Number(value);
  return `${parsed > 0 ? '+' : ''}${parsed.toFixed(2)}%`;
}

function compactMoney(value?: number | null) {
  if (value == null || !Number.isFinite(Number(value))) return '-';

  const parsed = Number(value);
  const abs = Math.abs(parsed);

  if (abs >= 1_0000_0000_0000) return `${(parsed / 1_0000_0000_0000).toFixed(1)}조`;
  if (abs >= 1_0000_0000) return `${(parsed / 1_0000_0000).toFixed(1)}억`;
  if (abs >= 1_0000) return `${(parsed / 1_0000).toFixed(1)}만`;

  return new Intl.NumberFormat('ko-KR').format(Math.round(parsed));
}

function formatMetricPct(value?: number | null) {
  if (value == null || !Number.isFinite(Number(value))) return '-';
  const parsed = Number(value);
  return `${parsed > 0 ? '+' : ''}${parsed.toFixed(1)}%`;
}

function formatLevelPct(value?: number | null) {
  if (value == null || !Number.isFinite(Number(value))) return '-';
  return `${Number(value).toFixed(1)}%`;
}

function formatMultiple(value?: number | null) {
  if (value == null || !Number.isFinite(Number(value)) || Number(value) <= 0) return '-';
  return `${Number(value).toFixed(2)}배`;
}

function formatAnyMultiple(value?: number | null) {
  if (value == null || !Number.isFinite(Number(value))) return '-';
  return `${Number(value).toFixed(2)}배`;
}

function financialValue(row: Record<string, any> | null | undefined, key: string): any {
  if (!row) return undefined;
  return row[key] ?? row[key.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`)];
}

function financialNumber(row: Record<string, any> | null | undefined, key: string): number | null {
  const raw = financialValue(row, key);
  if (raw === null || raw === undefined || raw === '') return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

function financialYearSeries(row: Record<string, any> | null | undefined) {
  if (!row) return [];
  const fy0 = Number(financialValue(row, 'fy0Year'));
  return [0, 1, 2, 3, 4]
    .map((index) => ({
      index,
      year: Number.isFinite(fy0) && fy0 >= 1900 ? String(fy0 - index) : `Y-${index}`,
      revenue: financialNumber(row, `revenueY${index}`),
      operatingIncome: financialNumber(row, `operatingIncomeY${index}`),
      netIncome: financialNumber(row, `netIncomeY${index}`),
      equity: financialNumber(row, `totalEquityY${index}`),
    }))
    .filter((item) => item.revenue !== null || item.operatingIncome !== null || item.netIncome !== null || item.equity !== null)
    .reverse();
}

// YoY is meaningful only between consecutive fiscal years. Never manufacture
// a growth percentage from missing years or a non-positive base period.
function consecutiveAnnualYoy(
  annual: Array<{ year: string; revenue: number | null; operatingIncome: number | null }>,
  field: 'revenue' | 'operatingIncome',
): number | null {
  if (annual.length < 2) return null;
  const latest = annual[annual.length - 1];
  const prior = annual[annual.length - 2];
  if (!/^\d{4}$/.test(latest.year) || !/^\d{4}$/.test(prior.year)) return null;
  if (Number(latest.year) - Number(prior.year) !== 1) return null;
  const current = latest[field];
  const base = prior[field];
  if (current == null || base == null || !Number.isFinite(current) || !Number.isFinite(base) || base <= 0) return null;
  return ((current / base) - 1) * 100;
}

function firstText(...values: any[]) {
  for (const value of values) {
    const text = S(value).trim();
    if (text && text !== 'null' && text !== 'undefined' && text !== '-') return text;
  }

  return '';
}

function fallbackFinancialRiskLabel(score?: number) {
  if (score == null) return '';
  if (score >= 75) return '낮음';
  if (score >= 55) return '보통';
  return '높음';
}

function fallbackFinancialOutlookLabel(score?: number) {
  if (score == null) return '';
  if (score >= 75) return '재무 양호';
  if (score >= 55) return '재무 보통';
  return '재무 주의';
}

function buildFinancialFallbackSummary(score?: number, riskLabel?: string) {
  if (score == null) {
    return '재무제표 요약 데이터가 없거나 아직 연결되지 않았습니다. 현재 판단은 가격·수급·거래대금 중심으로 해석하세요.';
  }

  if (score >= 75) {
    return '재무 점수 기준으로는 안정성이 양호한 편입니다. 다만 이 값은 진입 근거가 아니라 리스크 필터로만 참고하세요.';
  }

  if (score >= 55) {
    return '재무 점수 기준으로는 보통 수준입니다. 가격·수급 신호가 좋아도 재무 리스크를 함께 확인해야 합니다.';
  }

  return `재무 점수 기준 리스크가 ${riskLabel || '높은'} 편입니다. 정책·수급 신호가 있더라도 후보 우선순위를 낮춰 보는 것이 안전합니다.`;
}


function parseStringList(value: any): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => firstText(item)).filter(Boolean);
  }

  const text = firstText(value);
  if (!text) return [];

  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) {
      return parsed.map((item) => firstText(item)).filter(Boolean);
    }
  } catch {}

  return text
    .replace(/^\[|\]$/g, '')
    .split(/[,|·]/)
    .map((item) => item.replace(/^["']|["']$/g, '').trim())
    .filter(Boolean);
}

function businessProfileSource(raw: any): any {
  return (
    raw?.businessProfile ??
    raw?.business_profile ??
    raw?.stockBusinessProfile ??
    raw?.stock_business_profile ??
    raw?.business ??
    null
  );
}

function normalizeBusinessSummaryHeading(value: unknown): string {
  const text = firstText(value)
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!text) return '';

  return text
    .replace(/^[•·▪■◆▶▷►※\-–—]+\s*/u, '')
    .replace(
      /^(?:제\s*)?(?:\d+|[IVXLCDM]+|[ⅠⅡⅢⅣⅤⅥⅦⅧⅨⅩ]+)\s*(?:장|절|항)?\s*[.)\-:：]?\s*/iu,
      '',
    )
    .replace(/[\s.:：·ㆍ]/g, '')
    .toLowerCase();
}

function isBusinessSummaryPlaceholder(value: unknown): boolean {
  const normalized = normalizeBusinessSummaryHeading(value);
  if (!normalized) return true;

  const placeholders = new Set([
    '사업의개요',
    '사업개요',
    '사업의내용',
    '사업내용',
    '회사의개요',
    '회사개요',
    '회사의현황',
    '영업의개황',
    '영업개황',
    '주요사업',
    '주요사업의내용',
  ]);

  return placeholders.has(normalized);
}

function meaningfulBusinessSummary(...values: unknown[]): string {
  for (const value of values) {
    const text = firstText(value);
    if (!text) continue;
    if (isBusinessSummaryPlaceholder(text)) continue;
    return text;
  }

  return '';
}

function normalizeBusinessProfile(raw: any): StockBusinessProfileResponse | null {
  const profile = businessProfileSource(raw);
  // Detail APIs may return some business fields at the root and others nested.
  // A partial nested object must not hide valid flattened terms/industry data.
  const source = profile && typeof profile === 'object'
    ? { ...raw, ...Object.fromEntries(Object.entries(profile).filter(([, value]) => value != null && value !== '')) }
    : raw;

  const businessSummary = meaningfulBusinessSummary(
    source?.businessSummary,
    source?.business_summary,
    source?.businessOverview,
    source?.business_overview,
    source?.companySummary,
    source?.company_summary,
  );

  const businessReportSummary = meaningfulBusinessSummary(
    source?.businessReportSummary,
    source?.business_report_summary,
    source?.reportBusinessSummary,
    source?.report_business_summary,
    source?.businessDescription,
    source?.business_description,
  );

  const currentBusinessSummary = meaningfulBusinessSummary(
    source?.currentBusinessSummary,
    source?.current_business_summary,
    source?.currentBusinessReportSummary,
    source?.current_business_report_summary,
  );

  const industryL1 = firstText(source?.industryL1, source?.industry_l1);
  const industryL2 = firstText(source?.industryL2, source?.industry_l2);
  const industryL3 = firstText(source?.industryL3, source?.industry_l3);
  const primarySector = firstText(source?.primarySector, source?.primary_sector);
  const productTerms = parseStringList(
    source?.productTerms ??
    source?.product_terms ??
    source?.products ??
    source?.mainProducts ??
    source?.main_products,
  );
  const technologyTerms = parseStringList(
    source?.technologyTerms ??
    source?.technology_terms ??
    source?.technologyContextTerms ??
    source?.technology_context_terms,
  );
  const coreTerms = parseStringList(source?.coreTerms ?? source?.core_terms);
  const secondarySectors = parseStringList(source?.secondarySectors ?? source?.secondary_sectors);

  const hasMeaningfulData = Boolean(
    businessSummary ||
    businessReportSummary ||
    currentBusinessSummary ||
    industryL1 ||
    industryL2 ||
    industryL3 ||
    primarySector ||
    productTerms.length ||
    technologyTerms.length ||
    coreTerms.length,
  );

  if (!hasMeaningfulData) return null;

  return {
    ...(profile || {}),
    ticker: firstText(source?.ticker, raw?.ticker, raw?.norm_ticker, raw?.code) || null,
    corpName: firstText(source?.corpName, source?.corp_name, raw?.corpName, raw?.corp_name, raw?.name) || null,
    market: firstText(source?.market, raw?.market) || null,
    reportDate: firstText(source?.reportDate, source?.report_date, source?.baseDate, source?.base_date) || null,
    reportName: firstText(source?.reportName, source?.report_name, source?.reportNm, source?.report_nm) || null,
    rceptNo: firstText(source?.rceptNo, source?.rcept_no, source?.sourceRceptNo, source?.source_rcept_no) || null,
    industryL1,
    industryL2,
    industryL3,
    primarySector,
    secondarySectors,
    classificationConfidence: n(source?.classificationConfidence ?? source?.classification_confidence ?? source?.confidence),
    classificationReason: firstText(source?.classificationReason, source?.classification_reason) || null,
    businessSummary,
    business_summary: businessSummary || null,
    businessReportSummary,
    business_report_summary: businessReportSummary || null,
    currentBusinessSummary,
    current_business_summary: currentBusinessSummary || null,
    productTerms,
    technologyTerms,
    coreTerms,
    updatedAt: firstText(source?.updatedAt, source?.updated_at, source?.reportDate, source?.report_date) || null,
  };
}

function businessSummaryValueOf(row?: ExtendedStockRow): string {
  if (!row?.businessProfile) return '';

  return meaningfulBusinessSummary(
    row.businessProfile.businessSummary,
    row.businessProfile.business_summary,
    row.businessProfile.currentBusinessSummary,
    row.businessProfile.current_business_summary,
    row.businessProfile.businessReportSummary,
    row.businessProfile.business_report_summary,
  );
}

function businessSummaryOf(row?: ExtendedStockRow): string {
  if (!row?.businessProfile) {
    return '사업보고서 기반 사업 프로필이 아직 연결되지 않았습니다.';
  }

  return businessSummaryValueOf(row) || '사업보고서에서 상세 사업 설명을 확인할 수 없습니다.';
}

function businessIndustryPath(profile?: StockBusinessProfileResponse | null): string {
  if (!profile) return '-';
  return [
    firstText(profile.industryL1, profile.industry_l1),
    firstText(profile.industryL2, profile.industry_l2),
    firstText(profile.industryL3, profile.industry_l3),
  ].filter(Boolean).join(' > ') || '-';
}

function businessProductTerms(profile?: StockBusinessProfileResponse | null): string[] {
  if (!profile) return [];
  return parseStringList(profile.productTerms ?? profile.product_terms);
}

function businessTechnologyTerms(profile?: StockBusinessProfileResponse | null): string[] {
  if (!profile) return [];
  return parseStringList(profile.technologyTerms ?? profile.technology_terms);
}

function businessSecondarySectors(profile?: StockBusinessProfileResponse | null): string[] {
  if (!profile) return [];
  return parseStringList(profile.secondarySectors ?? profile.secondary_sectors);
}

function normalizeFinancialProfile(raw: any): StockFinancialProfileResponse | null {
  const profile = raw?.financialProfile ?? raw?.financial_profile ?? null;

  const score = n(
    raw?.financial_score ??
      raw?.financialScore ??
      raw?.finance_score ??
      raw?.financeScore ??
      profile?.financialScore ??
      profile?.financial_score ??
      profile?.score,
  );

  const financialGrade = firstText(
    raw?.financial_grade,
    raw?.financialGrade,
    profile?.financialGrade,
    profile?.financial_grade,
    fallbackFinancialOutlookLabel(score),
  );

  const financialRiskLevel = firstText(
    raw?.financial_risk_level,
    raw?.financialRiskLevel,
    raw?.financial_risk_label,
    raw?.financialRiskLabel,
    raw?.risk_level,
    raw?.riskLevel,
    raw?.risk_label,
    raw?.riskLabel,
    profile?.financialRiskLevel,
    profile?.financial_risk_level,
    profile?.riskLabel,
    profile?.risk_label,
    fallbackFinancialRiskLabel(score),
  );

  if (!profile && score == null && !financialGrade && !financialRiskLevel) return null;

  const summary = firstText(
    raw?.financial_summary,
    raw?.financialSummary,
    profile?.adjustedFinancialSummary,
    profile?.adjusted_financial_summary,
    profile?.gptFinancialSummary,
    profile?.gpt_financial_summary,
    buildFinancialFallbackSummary(score, financialRiskLevel),
  );

  return {
    ...(profile || {}),
    ticker: profile?.ticker ?? raw?.ticker ?? raw?.norm_ticker ?? raw?.code ?? null,
    corpName: profile?.corpName ?? profile?.corp_name ?? raw?.corp_name ?? raw?.corpName ?? raw?.name ?? null,
    financialScore: score ?? profile?.financialScore ?? profile?.financial_score ?? null,
    financialGrade,
    financial_grade: financialGrade,
    financialRiskLevel,
    financial_risk_level: financialRiskLevel,
    outlookLabel: financialGrade,
    riskLabel: financialRiskLevel,
    adjustedFinancialSummary: summary,
    updatedAt: firstText(raw?.financial_updated_at, raw?.financialUpdatedAt, profile?.updatedAt, profile?.updated_at) || null,
  };
}

function mapRow(raw: any): ExtendedStockRow {
  const financialProfile = normalizeFinancialProfile(raw);
  const businessProfile = normalizeBusinessProfile(raw);

  const financialScore = n(
    raw.financial_score ??
      raw.financialScore ??
      raw.finance_score ??
      raw.financeScore ??
      financialProfile?.financialScore,
  );

  const financialGrade = firstText(
    raw.financial_grade,
    raw.financialGrade,
    financialProfile?.financialGrade,
    financialProfile?.financial_grade,
    fallbackFinancialOutlookLabel(financialScore),
  );

  const financialRiskLevel = firstText(
    raw.financial_risk_level,
    raw.financialRiskLevel,
    financialProfile?.financialRiskLevel,
    financialProfile?.financial_risk_level,
    financialProfile?.riskLabel,
    fallbackFinancialRiskLabel(financialScore),
  );

  const financialRiskLabel = firstText(
    raw.financial_risk_level,
    raw.financialRiskLevel,
    raw.financial_risk_label,
    raw.financialRiskLabel,
    raw.risk_level,
    raw.riskLevel,
    raw.risk_label,
    raw.riskLabel,
    financialRiskLevel,
    financialProfile?.riskLabel,
  );

  const financialOutlookLabel = firstText(
    raw.financial_grade,
    raw.financialGrade,
    raw.financial_outlook_label,
    raw.financialOutlookLabel,
    raw.outlook_label,
    raw.outlookLabel,
    financialGrade,
    financialProfile?.outlookLabel,
  );

  const financialSummary = firstText(
    raw.financial_summary,
    raw.financialSummary,
    financialProfile?.adjustedFinancialSummary,
    financialProfile?.gptFinancialSummary,
    buildFinancialFallbackSummary(financialScore, financialRiskLabel),
  );

  return {
    ticker: S(raw.ticker ?? raw.norm_ticker ?? raw.code ?? ''),
    corp_name: S(raw.corp_name ?? raw.name ?? raw.corpName ?? ''),
    market: S(raw.market),
    date: S(raw.date),
    open: n(raw.open),
    high: n(raw.high),
    low: n(raw.low),
    close: n(raw.close ?? raw.currentPrice),
    prev_close: n(raw.prev_close ?? raw.prevclose ?? raw.prev ?? raw.yesterday_close),
    volume: n(raw.volume ?? raw.cumVolume),
    marketCap: n(raw.market_cap ?? raw.marketCap),
    judgmentMaintainPrice: n(
      raw.judgment_maintain_price ?? raw.judgmentMaintainPrice ??
      raw.decision_maintain_price ?? raw.decisionMaintainPrice ??
      raw.hold_line ?? raw.holdLine
    ),
    judgmentImprovePrice: n(
      raw.judgment_improve_price ?? raw.judgmentImprovePrice ??
      raw.decision_improve_price ?? raw.decisionImprovePrice ??
      raw.improvement_line ?? raw.improvementLine
    ),
    judgmentRiskPrice: n(
      raw.judgment_risk_price ?? raw.judgmentRiskPrice ??
      raw.decision_risk_price ?? raw.decisionRiskPrice ??
      raw.risk_line ?? raw.riskLine
    ),
    judgmentChangedCount: n(
      raw.judgment_changed_count ?? raw.judgmentChangedCount ??
      raw.judgment_change_count ?? raw.judgmentChangeCount
    ),
    price_change_percentage: n(raw.price_change_percentage ?? raw.change_pct ?? raw.pct_change ?? raw.changeRate),
    percentile: n(raw.percentile),
    rankChange: n(raw.rank_change),

    tradingValue: n(raw.trading_value ?? raw.tradingValue ?? raw.trading_value_day ?? raw.cumTradingValue),
    tradingValueShare: n(raw.trading_value_share ?? raw.tradingValueShare),
    tradingValueRank: n(raw.trading_value_rank ?? raw.tradingValueRank),
    liquidityTier: S(raw.liquidity_tier ?? raw.liquidityTier) as 'A' | 'B' | 'C',

    rsLocalPct: n(raw.rs_local_pct ?? raw.rsLocalPct),
    signalStrength: n(raw.signal_strength ?? raw.signalStrength),
    hasSpikeLike: !!(raw.has_spike_like ?? raw.hasSpikeLike),

    priceStage: S(raw.price_stage ?? raw.priceStage),
    flowStage: S(raw.flow_stage ?? raw.flowStage),
    crowdingStage: S(raw.crowding_stage ?? raw.crowdingStage),
    actionLabel: S(raw.action_label ?? raw.actionLabel),
    eventStage: S(raw.event_stage ?? raw.eventStage),
    timingState: S(raw.timing_state ?? raw.timingState),

    foreignZ: n(raw.foreign_z ?? raw.foreignZ),
    maGap20Pct: n(raw.ma_gap20_pct ?? raw.maGap20Pct),
    maGap60Pct: n(raw.ma_gap60_pct ?? raw.maGap60Pct),

    priorityScore: n(raw.priority_score ?? raw.priorityScore),
    contradictionScore: n(raw.contradiction_score ?? raw.contradictionScore),
    confidenceScore: n(raw.confidence_score ?? raw.confidenceScore),
    decisionLabel: S(raw.decision_label ?? raw.decisionLabel),
    trendLabel: S(raw.trend_label ?? raw.trendLabel),
    flowLabel: S(raw.flow_label ?? raw.flowLabel),

    policyCount7d: n(raw.policy_count_7d ?? raw.policyCount7d),
    directCount7d: n(raw.direct_count_7d ?? raw.directCount7d),
    indirectCount7d: n(raw.indirect_count_7d ?? raw.indirectCount7d),
    maxArticleImpactScore: n(raw.max_article_impact_score ?? raw.maxArticleImpactScore),
    avgArticleConfidence: n(raw.avg_article_confidence ?? raw.avgArticleConfidence),
    maxPolicyImpactScore: n(raw.max_policy_impact_score ?? raw.maxPolicyImpactScore),
    hasDirectPolicy: !!(raw.has_direct_policy ?? raw.hasDirectPolicy),
    hasIndirectPolicy: !!(raw.has_indirect_policy ?? raw.hasIndirectPolicy),

    reportSector: S(raw.report_sector ?? raw.reportSector),
    dailyIssueCount: n(raw.daily_issue_count ?? raw.dailyIssueCount),
    insightTitle: S(raw.insight_title ?? raw.insightTitle),
    insightSummary: S(raw.insight_summary ?? raw.insightSummary),
    insightInterpretation: S(raw.insight_interpretation ?? raw.insightInterpretation),
    insightRisk: S(raw.insight_risk ?? raw.insightRisk),
    insightAction: S(raw.insight_action ?? raw.insightAction),

    pbr: n(raw.pbr),
    per: n(raw.per),
    bps: n(raw.bps),
    eps: n(raw.eps),

    financialProfile,
    financialScore,
    financialGrade,
    financialRiskLevel,
    financialOutlookLabel,
    financialRiskLabel,
    financialSummary,
    financialUpdatedAt: S(raw.financial_updated_at ?? raw.financialUpdatedAt ?? financialProfile?.updatedAt),
    businessProfile,
  };
}

function trendLabel(row: ExtendedStockRow) {
  if (row.trendLabel) return row.trendLabel;

  const priceStage = S(row.priceStage).toUpperCase();
  const actionLabel = S(row.actionLabel).toUpperCase();
  const pct = row.price_change_percentage ?? 0;

  if (['BREAKOUT', 'EARLY', 'INITIAL_EXPANSION', 'EXPANSION', 'UPTREND'].includes(priceStage)) return '상승 추세';

  if (
    ['PULLBACK', 'PULLBACK_OR_BASE', 'WAIT_PULLBACK', 'SCALE_IN_ON_PULLBACK'].includes(priceStage) ||
    actionLabel === 'WAIT_PULLBACK'
  ) {
    return '조정 확인';
  }

  if (['TAKE_PROFIT', 'REDUCE', 'AVOID_CHASE', 'EXTENDED'].includes(actionLabel) || priceStage === 'EXTENDED') {
    return '과열 주의';
  }

  if (actionLabel === 'OBSERVE' || actionLabel === 'WATCH' || priceStage === 'PRE_BREAKOUT') return '관찰';
  if (row.signalStrength != null && row.signalStrength > 0.7 && pct >= 0) return '상승 추세';
  if (row.signalStrength != null && row.signalStrength < -0.2) return '약세';

  return '신호 혼조';
}

function flowLabel(row: ExtendedStockRow) {
  if (row.flowLabel) return row.flowLabel;

  const flowStage = S(row.flowStage).toUpperCase();

  if (['BROAD_ACCUMULATION', 'DUAL_ACCUM'].includes(flowStage)) return '동반 유입';
  if (['INSTITUTION_LEAD', 'INST_LEAD'].includes(flowStage)) return '기관 유입';
  if (flowStage === 'FOREIGN_LEAD') return '외인 유입';
  if (flowStage === 'DISTRIBUTION') return '매도 우세';

  if (row.foreignZ != null) {
    if (row.foreignZ >= 0.8) return '외인 강세';
    if (row.foreignZ <= -0.8) return '외인 약세';
  }

  return '수급 혼조';
}

function financialScoreOf(row?: ExtendedStockRow) {
  if (!row) return undefined;
  return row.financialScore ?? row.financialProfile?.financialScore ?? undefined;
}

function financialRiskOf(row?: ExtendedStockRow) {
  if (!row) return '';
  return firstText(
    row.financialRiskLevel,
    row.financialRiskLabel,
    row.financialProfile?.financialRiskLevel,
    row.financialProfile?.financial_risk_level,
    row.financialProfile?.riskLabel,
    fallbackFinancialRiskLabel(financialScoreOf(row)),
  );
}

function financialGradeOf(row?: ExtendedStockRow) {
  if (!row) return '';
  return firstText(
    row.financialGrade,
    row.financialOutlookLabel,
    row.financialProfile?.financialGrade,
    row.financialProfile?.financial_grade,
    row.financialProfile?.outlookLabel,
    fallbackFinancialOutlookLabel(financialScoreOf(row)),
  );
}

function financialSummaryOf(row?: ExtendedStockRow) {
  if (!row) return '';

  const score = financialScoreOf(row);
  const risk = financialRiskOf(row);

  return firstText(
    row.financialSummary,
    row.financialProfile?.adjustedFinancialSummary,
    row.financialProfile?.gptFinancialSummary,
    buildFinancialFallbackSummary(score, risk),
  );
}

function financialTone(row?: ExtendedStockRow): { label: string; tone: Tone } {
  const score = financialScoreOf(row);
  const grade = financialGradeOf(row);
  const risk = financialRiskOf(row);

  if (score == null) return { label: '재무 미확인', tone: 'slate' };
  if (risk.includes('높') || grade.includes('주의') || score < 50) return { label: grade || '재무 취약', tone: 'red' };
  if (score >= 70 && !risk.includes('높')) return { label: grade || '재무 양호', tone: 'green' };
  return { label: grade || '재무 보통', tone: 'amber' };
}

function inferDecisionBucket(row: ExtendedStockRow): DecisionBucket {
  const decision = S(row.decisionLabel);
  const action = S(row.actionLabel).toUpperCase();
  const trend = trendLabel(row);
  const contradiction = row.contradictionScore ?? 0;
  const priority = row.priorityScore ?? 0;
  const confidence = row.confidenceScore ?? 0;
  const gap20 = row.maGap20Pct ?? 0;
  const risk = financialRiskOf(row);

  if (/제외|회피|보류/.test(decision)) return 'Low';
  if (['TAKE_PROFIT', 'REDUCE', 'AVOID_CHASE', 'EXTENDED'].includes(action)) return 'Risk';
  if (contradiction >= 65 || gap20 >= 14 || S(risk).includes('높')) return 'Risk';
  if (/눌림|조정|과열/.test(decision) || trend === '과열 주의' || trend === '조정 확인') return 'Wait';
  if (priority >= 82 && confidence >= 58 && contradiction <= 35) return 'Action';
  if (priority >= 66 || row.hasDirectPolicy || (row.policyCount7d ?? 0) >= 2) return 'Review';

  return 'Review';
}

function decisionTone(bucket: DecisionBucket): Tone {
  if (bucket === 'Action') return 'blue';
  if (bucket === 'Review') return 'green';
  if (bucket === 'Wait') return 'amber';
  if (bucket === 'Risk') return 'red';
  return 'slate';
}

function policyScore(row: ExtendedStockRow) {
  return Math.round(
    Math.min(
      100,
      Math.max(
        row.maxPolicyImpactScore ?? 0,
        row.maxArticleImpactScore ?? 0,
        row.hasDirectPolicy ? 74 : 0,
        (row.policyCount7d ?? 0) * 14,
        (row.directCount7d ?? 0) * 22,
        (row.indirectCount7d ?? 0) * 10,
      ),
    ),
  );
}

function marketScore(row: ExtendedStockRow) {
  const priority = row.priorityScore ?? 0;
  const signal = (row.signalStrength ?? 0) * 100;
  const rs = row.rsLocalPct ?? 0;

  const tradingValue = row.tradingValue ?? 0;
  const volume = row.volume ?? 0;
  const changePct = row.price_change_percentage ?? 0;

  let liquidityScore = 0;

  if (tradingValue >= 1_0000_0000_0000) liquidityScore = 90;
  else if (tradingValue >= 3000_0000_0000) liquidityScore = 78;
  else if (tradingValue >= 1000_0000_0000) liquidityScore = 66;
  else if (tradingValue >= 300_0000_0000) liquidityScore = 54;
  else if (tradingValue >= 100_0000_0000) liquidityScore = 42;
  else if (tradingValue > 0) liquidityScore = 30;

  let volumeScore = 0;

  if (volume >= 10_000_000) volumeScore = 75;
  else if (volume >= 3_000_000) volumeScore = 65;
  else if (volume >= 1_000_000) volumeScore = 55;
  else if (volume >= 300_000) volumeScore = 42;
  else if (volume > 0) volumeScore = 28;

  const priceReactionScore =
    Math.abs(changePct) >= 10 ? 70 :
    Math.abs(changePct) >= 5 ? 58 :
    Math.abs(changePct) >= 3 ? 46 :
    Math.abs(changePct) >= 1 ? 34 :
    20;

  const fallbackScore = Math.round(
    liquidityScore * 0.45 +
      volumeScore * 0.25 +
      priceReactionScore * 0.30,
  );

  return Math.round(
    Math.min(
      100,
      Math.max(priority, signal, rs, fallbackScore),
    ),
  );
}

function riskScore(row: ExtendedStockRow) {
  const contradiction = row.contradictionScore ?? 0;
  const maGapRisk = (row.maGap20Pct ?? 0) > 12 ? 72 : 0;

  const financialScore = financialScoreOf(row);
  const financialRiskLabel = financialRiskOf(row);

  const financialRisk =
    financialScore == null ? 20 :
    financialRiskLabel.includes('높') ? 72 :
    financialScore < 45 ? 68 :
    financialScore < 55 ? 52 :
    financialScore < 70 ? 35 :
    20;

  const changePct = row.price_change_percentage ?? 0;

  const priceDropRisk =
    changePct <= -12 ? 80 :
    changePct <= -8 ? 68 :
    changePct <= -5 ? 55 :
    changePct <= -3 ? 42 :
    changePct < 0 ? 28 :
    15;

  const tradingValue = row.tradingValue ?? 0;

  const largeMoveRisk =
    tradingValue >= 1_0000_0000_0000 && changePct <= -5 ? 62 :
    tradingValue >= 3000_0000_0000 && changePct <= -5 ? 55 :
    0;

  return Math.round(
    Math.min(
      100,
      Math.max(
        contradiction,
        maGapRisk,
        financialRisk,
        priceDropRisk,
        largeMoveRisk,
      ),
    ),
  );
}

function finalScore(row: ExtendedStockRow) {
  const policy = policyScore(row);
  const market = marketScore(row);

  const flow =
    flowLabel(row).includes('동반') ? 88 :
    flowLabel(row).includes('외인') ? 74 :
    flowLabel(row).includes('기관') ? 70 :
    flowLabel(row).includes('약세') || flowLabel(row).includes('매도') ? 35 :
    52;

  const confidence = row.confidenceScore ?? row.avgArticleConfidence ?? 50;
  const risk = riskScore(row);

  return Math.round(
    policy * 0.23 +
      market * 0.3 +
      flow * 0.2 +
      confidence * 0.14 +
      Math.max(0, 100 - risk) * 0.13,
  );
}

function oneLineConclusion(row: ExtendedStockRow) {
  const bucket = inferDecisionBucket(row);
  const trend = trendLabel(row);
  const flow = flowLabel(row);

  if (row.insightInterpretation) return row.insightInterpretation;
  if (row.insightSummary) return row.insightSummary;

  if (bucket === 'Action') {
    return `${trend} 흐름과 ${flow} 신호가 동시에 확인되는 구간입니다. 가격 추격보다 거래대금과 수급 지속성을 먼저 확인하세요.`;
  }

  if (bucket === 'Review') {
    return '정책·수급 연결 가능성은 있으나 확정 구간은 아닙니다. 후속 이슈와 가격 위치를 같이 봐야 합니다.';
  }

  if (bucket === 'Wait') {
    return `${trend} 상태입니다. 신규 접근은 눌림과 지지 확인 이후가 더 합리적입니다.`;
  }

  if (bucket === 'Risk') {
    return '가격·수급·재무 중 하나 이상에서 경계 신호가 있습니다. 현재는 기회보다 리스크 확인이 먼저입니다.';
  }

  return '현재 우선순위에서는 낮습니다. 재부각 이슈나 수급 전환이 나올 때 다시 확인하세요.';
}

function coreReasons(row: ExtendedStockRow) {
  const reasons: string[] = [];

  if (row.hasDirectPolicy) reasons.push('직접 정책 연결');
  if ((row.directCount7d ?? 0) > 0) reasons.push(`직접 이슈 ${row.directCount7d}건`);
  if ((row.policyCount7d ?? 0) >= 2) reasons.push(`정책 이슈 ${row.policyCount7d}건`);
  if ((row.rsLocalPct ?? 0) >= 70) reasons.push('상대강도 상위');
  if ((row.signalStrength ?? 0) >= 0.8) reasons.push('관찰 강도 우위');
  if (row.liquidityTier === 'A') reasons.push('유동성 상');
  if (row.hasSpikeLike) reasons.push('거래 집중');
  if (flowLabel(row).includes('동반')) reasons.push('동반 수급');

  if (reasons.length === 0) reasons.push('가격·수급 데이터 기반 선별');

  return reasons.slice(0, 4);
}

function riskReasons(row: ExtendedStockRow) {
  const risks: string[] = [];

  if ((row.contradictionScore ?? 0) >= 55) risks.push('가격·수급 모순');
  if ((row.maGap20Pct ?? 0) > 12) risks.push('20일선 이격');
  if ((row.foreignZ ?? 0) <= -0.8) risks.push('외인 약세');
  if ((financialScoreOf(row) ?? 100) < 50) risks.push('재무 취약');
  if ((row.price_change_percentage ?? 0) <= -5) risks.push('급락 반응');
  if (inferDecisionBucket(row) === 'Risk') risks.push('추격주의');

  if (risks.length === 0) risks.push('수급 지속성 확인');

  return risks.slice(0, 3);
}

// A list snapshot may be from the last market session, even if the URL's
// end-date is a weekend. Details must use the resolved source date, not a
// calendar day for which the quick-bucket backend has no financial row.
function resolveQuickBucketDetailDate(
  requestedDate: string,
  currentTicker: string,
  candidates: ExtendedStockRow[],
): string {
  if (requestedDate !== today) return requestedDate; // preserve historical requests
  const ticker = normalizeTickerInput(currentTicker);
  const selected = candidates.find((row) => normalizeTickerInput(row.ticker) === ticker);
  const observed = S(selected?.date).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(observed) && observed <= requestedDate
    ? observed : requestedDate;
}

function buildListUrl(params: { to: string }) {
  const searchParams = new URLSearchParams();

  // quick-bucket-list의 서버 계약은 date/q/market/limit이다.
  // 검색/정렬/종목 구분 필터는 전체 후보를 받은 뒤 클라이언트에서 일관되게 처리한다.
  searchParams.set('date', params.to);
  searchParams.set('limit', String(PRELOAD_LIMIT));

  return `${QUICK_BUCKET_LIST_ENDPOINT}?${searchParams.toString()}`;
}

function clientSortRows(rows: ExtendedStockRow[], sort: SortKey, order: SortOrder) {
  const copied = [...rows];

  const pick = (row: ExtendedStockRow) => {
    if (sort === 'value') return row.tradingValue ?? 0;
    if (sort === 'volume') return row.volume ?? 0;
    if (sort === 'change') return row.price_change_percentage ?? 0;
    if (sort === 'policy') return policyScore(row);
    if (sort === 'financial') return financialScoreOf(row) ?? 0;
    if (sort === 'close') return row.close ?? 0;
    return finalScore(row);
  };

  copied.sort((a, b) => {
    const diff = pick(a) - pick(b);
    if (diff !== 0) return order === 'asc' ? diff : -diff;
    return S(a.corp_name).localeCompare(S(b.corp_name), 'ko');
  });

  return copied;
}

function stockMarketSegment(row: ExtendedStockRow): Exclude<StockMarketFilter, 'ALL'> {
  const market = S(row.market).trim().toUpperCase().replace(/\s+/g, '');

  if (market.includes('ETF')) return 'ETF';
  if (market.includes('ETN')) return 'ETN';
  if (market.includes('KOSDAQ')) return 'KOSDAQ';
  if (market.includes('KOSPI')) return 'KOSPI';
  if (market.includes('KONEX')) return 'KONEX';
  return 'OTHER';
}

function applyClientFilters(rows: ExtendedStockRow[], params: {
  query: string;
  marketFilter: StockMarketFilter;
}) {
  const keyword = S(params.query).trim();

  return rows.filter((row) => {
    if (params.marketFilter !== 'ALL' && stockMarketSegment(row) !== params.marketFilter) return false;

    if (!keyword) return true;

    return (
      contains(row.corp_name, keyword) ||
      contains(row.ticker, keyword) ||
      contains(row.reportSector, keyword) ||
      contains(row.insightTitle, keyword) ||
      contains(row.insightSummary, keyword)
    );
  });
}

function Badge({
  children,
  tone = 'slate',
}: {
  children: React.ReactNode;
  tone?: Tone;
}) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: `1px solid ${toneColor(tone)}44`,
        background: toneBg(tone),
        color: toneColor(tone),
        borderRadius: 999,
        padding: '5px 10px',
        fontSize: 12,
        fontWeight: 750,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}






function BusinessProfileTable({ row }: { row: ExtendedStockRow }) {
  const profile = row.businessProfile;
  const products = businessProductTerms(profile).slice(0, 10);
  // coreTerms are also tagged company terms. Do not hide them when
  // technologyTerms is absent in an otherwise valid business profile.
  const technologies = Array.from(new Set([
    ...businessTechnologyTerms(profile),
    ...parseStringList(profile?.coreTerms ?? profile?.core_terms),
  ])).slice(0, 8);
  const secondarySectors = businessSecondarySectors(profile).slice(0, 6);
  const confidence = n(profile?.classificationConfidence ?? profile?.classification_confidence);

  if (!profile) {
    return (
      <section style={styles.reasonBox}>
        <div style={styles.boxTitle}>사업 개요</div>
        <div style={styles.businessEmpty}>연결된 사업보고서 기반 프로필이 없습니다.</div>
      </section>
    );
  }

  const reportLabel = [
    firstText(profile.reportName, profile.report_name),
    firstText(profile.reportDate, profile.report_date),
  ].filter(Boolean).join(' · ');

  const primarySector = firstText(profile.primarySector, profile.primary_sector);
  const industryPath = businessIndustryPath(profile);
  const businessSummary = businessSummaryValueOf(row);
  const hasBusinessSummary = Boolean(businessSummary);

  return (
    <section className="pi-business-profile-card" style={styles.reasonBox}>
      <div className="pi-business-header" style={styles.businessHeader}>
        <div>
          <div style={styles.boxTitle}>사업 개요</div>
          <div style={styles.businessSubTitle}>사업보고서에서 핵심 사업·산업·제품 정보를 읽기 쉬운 순서로 정리했습니다.</div>
        </div>
        {primarySector ? <Badge tone="blue">{primarySector}</Badge> : null}
      </div>

      <div
        className="pi-business-summary-card"
        style={{
          ...styles.businessSummaryCard,
          ...(!hasBusinessSummary ? styles.businessSummaryCardMissing : {}),
        }}
      >
        <div className="pi-business-section-label" style={styles.businessSectionLabel}>무엇을 하는 회사인가</div>
        <p
          style={{
            ...styles.businessSummaryText,
            ...(!hasBusinessSummary ? styles.businessSummaryTextMissing : {}),
          }}
        >
          {businessSummaryOf(row)}
        </p>
      </div>

      <div className="pi-business-meta-grid" style={styles.businessMetaGrid}>
        <div className="pi-business-meta-item" style={styles.businessMetaItem}>
          <span>산업 분류</span>
          <strong>{industryPath}</strong>
        </div>
        <div className="pi-business-meta-item" style={styles.businessMetaItem}>
          <span>기준 보고서</span>
          <strong>{reportLabel || '-'}</strong>
        </div>
        <div className="pi-business-meta-item" style={styles.businessMetaItem}>
          <span>분류 신뢰도</span>
          <strong>{confidence != null ? `${(confidence <= 1 ? confidence * 100 : confidence).toFixed(1)}%` : '-'}</strong>
        </div>
      </div>

      {products.length ? (
        <div className="pi-business-chip-section" style={styles.businessChipSection}>
          <div className="pi-business-section-label" style={styles.businessSectionLabel}>주요 제품·서비스</div>
          <div className="pi-business-chip-row" style={styles.businessChipRow}>
            {products.map((item) => <span key={item} className="pi-business-chip" style={styles.businessChip}>{item}</span>)}
          </div>
        </div>
      ) : null}

      {technologies.length ? (
        <div className="pi-business-chip-section" style={styles.businessChipSection}>
          <div className="pi-business-section-label" style={styles.businessSectionLabel}>기술·사업 키워드</div>
          <div className="pi-business-chip-row" style={styles.businessChipRow}>
            {technologies.map((item) => <span key={item} className="pi-business-chip pi-business-chip--tech" style={{ ...styles.businessChip, ...styles.businessChipTech }}>{item}</span>)}
          </div>
        </div>
      ) : (
        <div className="pi-business-chip-section" style={styles.businessChipSection}>
          <div className="pi-business-section-label" style={styles.businessSectionLabel}>기술·사업 키워드</div>
          <div style={styles.businessEmpty}>확인된 키워드가 없습니다. 사업보고서 원천 또는 분류 결과를 확인하세요.</div>
        </div>
      )}

      {secondarySectors.length ? (
        <div className="pi-business-chip-section" style={styles.businessChipSection}>
          <div className="pi-business-section-label" style={styles.businessSectionLabel}>연관 섹터</div>
          <div className="pi-business-chip-row" style={styles.businessChipRow}>
            {secondarySectors.map((item) => <span key={item} className="pi-business-chip pi-business-chip--muted" style={{ ...styles.businessChip, ...styles.businessChipMuted }}>{item}</span>)}
          </div>
        </div>
      ) : null}

      {firstText(profile.classificationReason, profile.classification_reason) ? (
        <div style={styles.businessReason}>
          분류 근거: {firstText(profile.classificationReason, profile.classification_reason)}
        </div>
      ) : null}
    </section>
  );
}



function decisionLabelKo(bucket: DecisionBucket) {
  if (bucket === 'Action') return '우선 검토';
  if (bucket === 'Review') return '조건부 검토';
  if (bucket === 'Wait') return '관찰';
  return '제외';
}

function valuationCandidateLabel(row: ExtendedStockRow): string {
  const per = row.per;
  const pbr = row.pbr;
  if ((per != null && per > 0 && per <= 15) || (pbr != null && pbr > 0 && pbr <= 1.5)) return '가치 여유';
  if ((per != null && per >= 35) || (pbr != null && pbr >= 4)) return '가치 부담';
  if (per == null && pbr == null) return '가치 확인';
  return '가치 보통';
}

function trendArrow(row: ExtendedStockRow): '↑' | '→' | '↓' {
  const label = trendLabel(row);
  if (/상승|회복|돌파/.test(label)) return '↑';
  if (/약세|이탈|하락|과열/.test(label)) return '↓';
  return '→';
}

function flowArrow(row: ExtendedStockRow): '↑' | '→' | '↓' {
  const label = flowLabel(row);
  if (/유입|강세|축적|개선/.test(label)) return '↑';
  if (/약세|매도|이탈|분산/.test(label)) return '↓';
  return '→';
}

function decisionPriceLines(row: ExtendedStockRow) {
  return [
    row.judgmentMaintainPrice != null
      ? { label: '판단 유지선', price: row.judgmentMaintainPrice, tone: 'blue' as const }
      : null,
    row.judgmentImprovePrice != null
      ? { label: '개선 확인선', price: row.judgmentImprovePrice, tone: 'green' as const }
      : null,
    row.judgmentRiskPrice != null
      ? { label: '위험 기준선', price: row.judgmentRiskPrice, tone: 'red' as const }
      : null,
  ].filter((item): item is { label: string; price: number; tone: 'blue' | 'green' | 'red' } => Boolean(item));
}

function NavigatorRow({
  row,
  selected,
  compact = false,
  onSelect,
}: {
  row: ExtendedStockRow;
  selected: boolean;
  compact?: boolean;
  onSelect: () => void;
}) {
  const bucket = inferDecisionBucket(row);
  const tone = decisionTone(bucket);
  const change = row.price_change_percentage ?? 0;
  const changeColor = change > 0 ? UI.red : change < 0 ? UI.blue : UI.textSub;
  const navigatorBorderColor = selected ? UI.blue : UI.border;

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      style={{
        width: compact ? 148 : '100%',
        minWidth: compact ? 148 : 0,
        minHeight: compact ? 66 : 64,
        textAlign: 'left',
        // React 19 warns when border shorthand and borderBottom are mixed across rerenders.
        // Keep every side explicit so compact/non-compact transitions cannot conflict.
        borderTopWidth: compact ? 1 : 0,
        borderRightWidth: compact ? 1 : 0,
        borderBottomWidth: 1,
        borderLeftWidth: compact ? 1 : 0,
        borderTopStyle: 'solid',
        borderRightStyle: 'solid',
        borderBottomStyle: 'solid',
        borderLeftStyle: 'solid',
        borderTopColor: compact ? navigatorBorderColor : 'transparent',
        borderRightColor: compact ? navigatorBorderColor : 'transparent',
        borderBottomColor: compact ? navigatorBorderColor : UI.border,
        borderLeftColor: compact ? navigatorBorderColor : 'transparent',
        borderRadius: compact ? 12 : 0,
        background: selected ? '#f6f9fc' : '#fff',
        padding: compact ? '10px 11px' : '10px 12px',
        cursor: 'pointer',
        boxSizing: 'border-box',
        display: 'grid',
        gap: 7,
        position: 'relative',
        transition: 'background .15s ease, border-color .15s ease',
        boxShadow: compact && selected ? '0 0 0 1px rgba(15,59,95,.06)' : 'none',
      }}
    >
      {!compact && selected ? (
        <span style={{ position: 'absolute', left: 0, top: 9, bottom: 9, width: 3, borderRadius: 99, background: UI.blue }} />
      ) : null}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 8, alignItems: 'start' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ color: UI.text, fontSize: compact ? 12 : 12.5, fontWeight: 900, lineHeight: 1.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {row.corp_name}
          </div>
          <div style={{ color: UI.textMuted, fontSize: 9.5, fontWeight: 750, marginTop: 3, letterSpacing: '.02em' }}>
            {row.ticker}{row.market ? ` · ${row.market}` : ''}
          </div>
        </div>
        <strong style={{ color: changeColor, fontSize: compact ? 10.5 : 11, fontWeight: 900, whiteSpace: 'nowrap' }}>
          {formatPct(change)}
        </strong>
      </div>

      <div style={{ display: 'grid', gap: 3, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
          <span style={{ color: toneColor(tone), fontSize: 9.5, fontWeight: 900, whiteSpace: 'nowrap' }}>
            {decisionLabelKo(bucket)}
          </span>
          <span style={{ color: UI.textSub, fontSize: 9.1, fontWeight: 800, whiteSpace: 'nowrap' }}>
            가격 {trendArrow(row)} · 수급 {flowArrow(row)}
          </span>
        </div>
        <span style={{ color: UI.textMuted, fontSize: 8.9, fontWeight: 750, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {row.judgmentChangedCount != null && row.judgmentChangedCount > 0
            ? `판단 변화 ${Math.round(row.judgmentChangedCount)}건`
            : row.eventStage
              ? `상태 ${String(row.eventStage).replace(/_/g, ' ')}`
              : '최근 판단 유지'}
        </span>
      </div>
    </button>
  );
}

function WorkspaceNavigator({
  rows,
  selectedTicker,
  mobile = false,
  onSelect,
}: {
  rows: ExtendedStockRow[];
  selectedTicker?: string;
  mobile?: boolean;
  onSelect: (ticker: string) => void;
}) {
  if (!rows.length) {
    return <div style={styles.navigatorEmpty}>표시할 후보가 없습니다.</div>;
  }

  if (mobile) {
    return (
      <div style={styles.mobileTickerRail}>
        {rows.slice(0, 24).map((row) => (
          <NavigatorRow
            key={row.ticker}
            row={row}
            compact
            selected={norm(selectedTicker) === norm(row.ticker)}
            onSelect={() => onSelect(row.ticker)}
          />
        ))}
      </div>
    );
  }

  return (
    <div style={styles.navigatorList}>
      {rows.map((row) => (
        <NavigatorRow
          key={row.ticker}
          row={row}
          selected={norm(selectedTicker) === norm(row.ticker)}
          onSelect={() => onSelect(row.ticker)}
        />
      ))}
    </div>
  );
}

function WorkspaceInsightPanel({
  row,
  loading,
  error,
  compact = false,
}: {
  row?: ExtendedStockRow;
  loading: boolean;
  error: string;
  compact?: boolean;
}) {
  if (!row) {
    return (
      <section className="pi-intel-evidence-card is-empty">
        <strong>판단 근거를 준비 중입니다.</strong>
        <span>종목을 선택하면 선정 근거와 확인할 위험을 표시합니다.</span>
      </section>
    );
  }

  const bucket = inferDecisionBucket(row);
  const tone = decisionTone(bucket);
  const reasons = coreReasons(row).slice(0, compact ? 3 : 4);
  const risks = riskReasons(row).slice(0, compact ? 3 : 4);
  const pScore = policyScore(row);
  const mScore = marketScore(row);
  const rScore = riskScore(row);

  return (
    <section className="pi-intel-evidence-card">
      <div className="pi-intel-evidence-head">
        <div>
          <span className="pi-intel-kicker">WHY THIS VIEW</span>
          <h3>판단 근거</h3>
        </div>
        <Badge tone={tone}>{decisionLabelKo(bucket)}</Badge>
      </div>

      {loading ? <div className="pi-intel-inline-notice">상세 데이터를 불러오는 중입니다.</div> : null}
      {error ? <div className="pi-intel-inline-notice is-error">{error}</div> : null}

      <div className="pi-intel-score-row">
        <span>정책 <b>{pScore}</b></span>
        <span>시장 <b>{mScore}</b></span>
        <span>위험 <b>{rScore}</b></span>
      </div>

      <div className={`pi-intel-evidence-grid ${compact ? 'is-compact' : ''}`}>
        <div>
          <span className="pi-intel-evidence-label is-positive">선정 근거</span>
          <ul>
            {reasons.map((reason) => <li key={reason}>{reason}</li>)}
          </ul>
        </div>
        <div>
          <span className="pi-intel-evidence-label is-warning">확인할 위험</span>
          <ul>
            {risks.map((risk) => <li key={risk}>{risk}</li>)}
          </ul>
        </div>
      </div>
    </section>
  );
}


function BusinessFinancialPanel({
  row,
  loading,
  error,
  compact = false,
  mode = 'full',
  screenerFinancial,
  screenerLoading = false,
  screenerError = '',
  financialHistory = [],
  financialHistoryLoading = false,
  financialHistoryError = '',
  financialHistoryBasis = '',
  financialHistoryLatestYear = '',
}: {
  row?: ExtendedStockRow;
  loading: boolean;
  error: string;
  compact?: boolean;
  mode?: 'context' | 'full';
  screenerFinancial?: Record<string, any> | null;
  screenerLoading?: boolean;
  screenerError?: string;
  financialHistory?: ProfessionalFinancialHistoryItem[];
  financialHistoryLoading?: boolean;
  financialHistoryError?: string;
  financialHistoryBasis?: string;
  financialHistoryLatestYear?: string;
}) {
  if (!row) return null;

  const profile = row.financialProfile;
  const fTone = financialTone(row);
  const score = financialScoreOf(row);
  const cacheAnnual = financialYearSeries(screenerFinancial);
  const basisYear = String(
    financialHistoryLatestYear || financialValue(screenerFinancial, 'fy0Year') || profile?.bsnsYear || '',
  ).trim();
  const basisText = String(financialValue(screenerFinancial, 'financialBasisText') ?? '').trim();
  const basisWarning = String(
    financialValue(screenerFinancial, 'financialBasisWarningText') ??
    financialValue(screenerFinancial, 'fcfWarningText') ??
    '',
  ).trim();

  const directAnnual = financialHistory
    .map((item, index) => ({
      index,
      year: String(item?.year ?? ''),
      revenue: item?.revenue ?? null,
      operatingIncome: item?.operatingIncome ?? null,
      netIncome: item?.netIncome ?? null,
      assets: item?.totalAssets ?? null,
      liabilities: item?.totalLiabilities ?? null,
      equity: item?.totalEquity ?? null,
      operatingCashFlow: item?.operatingCashFlow ?? null,
      capex: item?.capex ?? null,
      cashAndEquivalents: item?.cashAndEquivalents ?? null,
      totalBorrowings: item?.totalBorrowings ?? null,
      netDebt: item?.netDebt ?? null,
      freeCashFlow: item?.freeCashFlow ?? null,
      ocfMarginPct: item?.ocfMarginPct ?? null,
      capexToRevenuePct: item?.capexToRevenuePct ?? null,
      fcfMarginPct: item?.fcfMarginPct ?? null,
      ocfToNetIncome: item?.ocfToNetIncome ?? null,
      interestCoverageAnnual: item?.interestCoverageAnnual ?? null,
      netDebtToOperatingIncomeAnnual: item?.netDebtToOperatingIncomeAnnual ?? null,
    }))
    .filter((item) => /^\d{4}$/.test(item.year))
    .sort((a, b) => Number(a.year) - Number(b.year));

  const annual = directAnnual.length
    ? directAnnual
    : cacheAnnual.map((item) => ({
        ...item,
        assets: null,
        liabilities: null,
        operatingCashFlow: null,
        capex: null,
        cashAndEquivalents: null,
        totalBorrowings: null,
        netDebt: null,
        freeCashFlow: null,
        ocfMarginPct: null,
        capexToRevenuePct: null,
        fcfMarginPct: null,
        ocfToNetIncome: null,
        interestCoverageAnnual: null,
        netDebtToOperatingIncomeAnnual: null,
      }));

  const latestAnnual = directAnnual.length ? directAnnual[directAnnual.length - 1] : null;
  const previousAnnual = directAnnual.length > 1 ? directAnnual[directAnnual.length - 2] : null;
  const borrowingYoyPct = latestAnnual?.totalBorrowings != null && previousAnnual?.totalBorrowings != null && previousAnnual.totalBorrowings !== 0
    ? ((latestAnnual.totalBorrowings / previousAnnual.totalBorrowings) - 1) * 100
    : null;
  const qualityYearCount = directAnnual.length;
  const positiveFcfYears = directAnnual.filter((item) => item.freeCashFlow != null && item.freeCashFlow > 0).length;
  const positiveOcfYears = directAnnual.filter((item) => item.operatingCashFlow != null && item.operatingCashFlow > 0).length;

  // Screener snapshots may not contain the YoY columns even when verified
  // consecutive-year annual financial statements are already available.
  // Prefer the same-source consecutive annual statements over a potentially
  // different fiscal period / stale professional screener snapshot.
  const revenueYoy = consecutiveAnnualYoy(directAnnual, 'revenue')
    ?? financialNumber(screenerFinancial, 'revenueGrowthYoyPct');
  const operatingYoy = consecutiveAnnualYoy(directAnnual, 'operatingIncome')
    ?? financialNumber(screenerFinancial, 'operatingIncomeGrowthYoyPct');
  const annualOperatingMargin = latestAnnual?.revenue != null && latestAnnual.revenue > 0 && latestAnnual.operatingIncome != null
    ? 100 * latestAnnual.operatingIncome / latestAnnual.revenue : null;
  const annualDebtRatio = latestAnnual?.equity != null && latestAnnual.equity > 0 && latestAnnual.liabilities != null
    ? 100 * latestAnnual.liabilities / latestAnnual.equity : null;

  const summaryMetrics = [
    { label: '재무 점수', value: score != null ? score.toFixed(1) : '-' },
    { label: '매출 YoY', value: formatMetricPct(revenueYoy) },
    { label: '영업익 YoY', value: formatMetricPct(operatingYoy) },
    { label: '영업이익률', value: formatLevelPct(annualOperatingMargin ?? financialNumber(screenerFinancial, 'operatingMarginPct') ?? profile?.operatingMargin ?? null) },
    { label: 'ROIC-WACC', value: formatMetricPct(financialNumber(screenerFinancial, 'actualRoicWaccSpreadPctp')) },
    { label: 'FCF Yield', value: formatLevelPct(financialNumber(screenerFinancial, 'fcfYieldPct')) },
  ];

  const fullMetrics = [
    ...summaryMetrics,
    { label: '매출 3Y CAGR', value: formatMetricPct(financialNumber(screenerFinancial, 'revenueCagr3yPct')) },
    { label: 'ROE', value: formatLevelPct(financialNumber(screenerFinancial, 'roePct')) },
    { label: 'ROIC', value: formatLevelPct(financialNumber(screenerFinancial, 'actualRoicPct') ?? financialNumber(screenerFinancial, 'roicProxyPct')) },
    { label: '부채비율', value: formatLevelPct(annualDebtRatio ?? financialNumber(screenerFinancial, 'debtToEquityPct') ?? profile?.debtRatio ?? null) },
    { label: '유동비율', value: formatMultiple(financialNumber(screenerFinancial, 'currentRatio')) },
    { label: '이자보상', value: formatMultiple(financialNumber(screenerFinancial, 'interestCoverage')) },
  ];

  const qualityMetrics = latestAnnual ? [
    { label: '현금전환 OCF/순이익', value: formatAnyMultiple(latestAnnual.ocfToNetIncome) },
    { label: 'OCF Margin', value: formatLevelPct(latestAnnual.ocfMarginPct) },
    { label: 'FCF Margin', value: formatLevelPct(latestAnnual.fcfMarginPct) },
    { label: 'CAPEX/매출', value: formatLevelPct(latestAnnual.capexToRevenuePct) },
    { label: '현금', value: compactMoney(latestAnnual.cashAndEquivalents) },
    { label: '총차입금', value: compactMoney(latestAnnual.totalBorrowings) },
    { label: '순차입금', value: compactMoney(latestAnnual.netDebt) },
    { label: '차입금 YoY', value: formatMetricPct(borrowingYoyPct) },
    { label: '연간 이자보상', value: formatAnyMultiple(latestAnnual.interestCoverageAnnual) },
    { label: '순차입금/영업익', value: formatAnyMultiple(latestAnnual.netDebtToOperatingIncomeAnnual) },
    { label: 'FCF 양수 연도', value: qualityYearCount ? `${positiveFcfYears}/${qualityYearCount}년` : '-' },
    { label: '영업CF 양수 연도', value: qualityYearCount ? `${positiveOcfYears}/${qualityYearCount}년` : '-' },
  ] : [];

  return (
    <section className="pi-business-financial-panel" style={{ ...styles.businessFinancialPanel, ...(compact ? styles.businessFinancialPanelMobile : {}) }}>
      <div className="pi-business-financial-header" style={styles.businessFinancialHeader}>
        <div>
          <div style={styles.businessFinancialEyebrow}>BUSINESS & FINANCIAL</div>
          <h3 style={styles.businessFinancialTitle}>{mode === 'context' ? '사업·재무 컨텍스트' : '기업 분석'}</h3>
          <p style={styles.businessFinancialSub}>
            {mode === 'context'
              ? '차트 판단에 필요한 사업 배경과 최근 실적 흐름을 함께 봅니다.'
              : '사업 근거와 재무 숫자를 분리하고, 실적 차트에서 재무제표까지 같은 흐름으로 확인합니다.'}
          </p>
        </div>
        <div className="pi-business-financial-badges" style={styles.businessFinancialHeaderBadges}>
          <Badge tone={fTone.tone}>{fTone.label}</Badge>
          <span style={styles.businessFinancialRisk}>위험도 {financialRiskOf(row) || '-'}</span>
          {basisYear ? <span style={styles.businessFinancialRisk}>연간재무 {basisYear}</span> : null}
        </div>
      </div>

      {loading ? <div style={styles.insightNotice}>종목 상세 데이터를 불러오는 중입니다.</div> : null}
      {error ? <div style={{ ...styles.insightNotice, color: UI.red }}>{error}</div> : null}
      {screenerLoading ? <div style={styles.insightNotice}>재무 스크리너 스냅샷을 불러오는 중입니다.</div> : null}
      {screenerError ? <div style={{ ...styles.insightNotice, color: UI.amber }}>{screenerError}</div> : null}

      {mode === 'full' ? (
        <BusinessProfileTable row={row} />
      ) : null}

      <FinancialAnalyticsPanel
        annual={annual}
        basis={financialHistoryBasis || basisText || '연간 사업보고서 · CFS 우선'}
        warning={basisWarning}
        latestYear={basisYear}
        loading={financialHistoryLoading}
        error={financialHistoryError}
        metrics={mode === 'context' ? summaryMetrics : fullMetrics}
        qualityMetrics={mode === 'full' ? qualityMetrics : []}
        compact={compact}
        full={mode === 'full'}
        title={mode === 'context' ? '실적 흐름' : '실적'}
        subtitle={mode === 'context'
          ? '매출·영업이익·순이익의 방향을 가격 차트 아래에서 바로 확인합니다.'
          : '연도별 실적을 먼저 보고, 아래에서 손익계산서·재무상태표·현금흐름표를 펼쳐 확인합니다.'}
      />

      <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 10, background: UI.cardSoft, border: `1px solid ${UI.border}` }}>
        <div style={styles.boxTitle}>PolicyIntel 재무 해석</div>
        <p style={{ ...styles.financialText, marginTop: 6 }}>{financialSummaryOf(row)}</p>
      </div>
    </section>
  );
}

function StockIssuePanel({ row }: { row?: ExtendedStockRow }) {
  if (!row) return null;

  const issueCount = Math.max(0, Number(row.dailyIssueCount ?? row.policyCount7d ?? 0) || 0);
  const items = [
    ['핵심 이슈', firstText(row.insightTitle)],
    ['요약', firstText(row.insightSummary)],
    ['해석', firstText(row.insightInterpretation)],
    ['위험', firstText(row.insightRisk)],
    ['다음 확인', firstText(row.insightAction)],
  ].filter(([, value]) => Boolean(value));

  return (
    <section style={styles.stockIssuePanel}>
      <div style={styles.stockIssueHead}>
        <div>
          <div style={styles.stockIssueEyebrow}>ISSUE & DISCLOSURE</div>
          <h3 style={styles.stockIssueTitle}>이슈 · 공시 인사이트</h3>
          <p style={styles.stockIssueSub}>현재 보유 데이터에서 종목 판단에 직접 연결되는 내용만 정리합니다.</p>
        </div>
        <Badge tone={issueCount > 0 ? 'blue' : 'slate'}>{issueCount > 0 ? `${issueCount}건` : '최근 이슈 없음'}</Badge>
      </div>

      {items.length ? (
        <div style={styles.stockIssueList}>
          {items.map(([label, value]) => (
            <article key={label} style={styles.stockIssueItem}>
              <div style={styles.stockIssueLabel}>{label}</div>
              <div style={styles.stockIssueText}>{value}</div>
            </article>
          ))}
        </div>
      ) : (
        <div style={styles.stockIssueEmpty}>현재 연결된 종목 이슈 요약이 없습니다.</div>
      )}
    </section>
  );
}

const StockCategoryPage: React.FC = () => {
  const location = useLocation();
  const isViewportMobile = useIsMobile(820);
  const isCompactDesktop = useIsMobile(1440);
  const isMobile = isViewportMobile;

  const [sort, setSort] = useQueryState<SortKey>(
    'sort',
    'value',
    (value) => (['decision', 'volume', 'close', 'change', 'value', 'policy', 'financial'].includes(value) ? value as SortKey : 'value'),
    (value) => String(value),
  );

  const [order, setOrder] = useQueryState<SortOrder>(
    'order',
    'desc',
    (value) => (value === 'asc' ? 'asc' : 'desc'),
    (value) => String(value),
  );

  const [view, setView] = useQueryState<MarketView>(
    'view',
    'candles',
    (value) => (['net', 'vol', 'price-only', 'candles'].includes(value) ? value as MarketView : 'candles'),
    (value) => String(value),
  );

  const [from, setFrom] = useQueryState<string>('from', defaultFrom, (value) => value, (value) => String(value));
  const [to, setTo] = useQueryState<string>('to', today, (value) => value, (value) => String(value));
  const [marketFilter, setMarketFilter] = useQueryState<StockMarketFilter>(
    'marketGroup',
    'ALL',
    (value) => (['ALL', 'KOSPI', 'KOSDAQ', 'ETF', 'ETN', 'KONEX', 'OTHER'].includes(value) ? value as StockMarketFilter : 'ALL'),
    (value) => String(value),
  );
  const [page, setPage] = useQueryState<number>('page', 1, (value) => Math.max(1, Number(value) || 1), (value) => String(value));

  const [nameQuery, setNameQuery] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  const [rows, setRows] = useState<ExtendedStockRow[]>([]);
  // A successful (including empty) list fetch is authoritative for a given
  // requested day. Prevent the detail effect from racing the first list fetch.
  const [rowsReadyForDate, setRowsReadyForDate] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const [selectedTicker, setSelectedTicker] = useState<string | undefined>(undefined);
  const [expandTicker, setExpandTicker] = useState<string | undefined>(undefined);
  const [selectedDetailRow, setSelectedDetailRow] = useState<ExtendedStockRow | undefined>(undefined);
  const [activeDate, setActiveDate] = useState<string | null>(null);
  const [stockDetailLoading, setStockDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [screenerFinancial, setScreenerFinancial] = useState<Record<string, any> | null>(null);
  const [screenerFinancialLoading, setScreenerFinancialLoading] = useState(false);
  const [screenerFinancialError, setScreenerFinancialError] = useState('');
  const [financialHistory, setFinancialHistory] = useState<ProfessionalFinancialHistoryItem[]>([]);
  const [financialHistoryLoading, setFinancialHistoryLoading] = useState(false);
  const [financialHistoryError, setFinancialHistoryError] = useState('');
  const [financialHistoryBasis, setFinancialHistoryBasis] = useState('');
  const [financialHistoryLatestYear, setFinancialHistoryLatestYear] = useState('');
  // Explicit user re-search retries failed/missing resources even if ticker is unchanged.
  const [financialHistoryRetryToken, setFinancialHistoryRetryToken] = useState(0);
  const [screenerRetryToken, setScreenerRetryToken] = useState(0);
  const [detailRetryToken, setDetailRetryToken] = useState(0);
  const [showFullTable, setShowFullTable] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [candidateRailOpen, setCandidateRailOpen] = useState(false);
  const [detailTab, setDetailTab] = useState<StockDetailTab>('overview');

  const lastDetailSignatureRef = useRef('');
  const routeSelectedTickerRef = useRef<string | undefined>(undefined);
  const lastFetchedDetailKeyRef = useRef('');
  const financialDetailRefreshKeyRef = useRef('');
  const detailRequestSeqRef = useRef(0);
  const detailAbortRef = useRef<AbortController | null>(null);
  const stockSearchRequestSeqRef = useRef(0);
  const canonicalSearchSelectionRef = useRef<{ ticker: string; displayName: string } | null>(null);
  const consumedRouteStateTickerRef = useRef('');
  const allowInitialAutoSelectRef = useRef(true);

  useEffect(() => {
    return () => {
      detailRequestSeqRef.current += 1;
      stockSearchRequestSeqRef.current += 1;
      detailAbortRef.current?.abort();
      detailAbortRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!piChartTracingEnabled() || typeof PerformanceObserver === 'undefined') return;
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.entryType !== 'resource') continue;
        const resource = entry as PerformanceResourceTiming;
        try {
          const endpoint = new URL(resource.name, window.location.href).pathname;
          if (!endpoint.startsWith('/api/')) continue;
          piChartTrace('resource:timing', {
            endpoint, durationMs: Math.round(resource.duration * 10) / 10,
            initiator: resource.initiatorType,
            encodedBytes: resource.encodedBodySize || 0,
            transferredBytes: resource.transferSize || 0,
          });
        } catch {}
      }
    });
    try { observer.observe({ type: 'resource' }); } catch { observer.disconnect(); return; }
    return () => observer.disconnect();
  }, []);

  const [indicators, setIndicators] = useState<IndicatorState & ExtraIndicatorFlags>({
    sma5: false,
    sma10: false,
    sma20: true,
    sma60: true,
    sma120: false,
    sma240: false,
    ema12: false,
    ema26: false,
    rsi14: false,
    macd: false,
    cumNet: false,
    vwap: false,
    bbands: false,
    adx: false,
    obv: false,
    vfi: false,
    mfi: false,
    volSMA5: false,
    volSMA20: false,
    volSpike: false,
    buySellRatio: false,
    lci: false,
  });

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const clean = normalizeStockSearchText(nameQuery);
      const selected = canonicalSearchSelectionRef.current;

      // A selected suggestion is filtered by canonical ticker, not by a display-name
      // spelling that can differ between the search master and quick-bucket payload.
      if (selected && stockNameKey(selected.displayName) === stockNameKey(clean)) {
        setDebouncedQ(selected.ticker);
        return;
      }

      if (selected) canonicalSearchSelectionRef.current = null;
      setDebouncedQ(clean);
    }, 180);

    return () => window.clearTimeout(timer);
  }, [nameQuery]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const q0 = (params.get('q') ?? '').trim();
    const exp0 = (params.get('expand') ?? '').trim();
    const urlTicker0 = (params.get('ticker') ?? '').trim();
    const routeStateTicker = firstText(
      (location.state as any)?.ticker,
      (location.state as any)?.selectedTicker,
    ).trim();

    // location.state는 외부 화면에서 처음 진입할 때만 보조 authority로 사용한다.
    // 초기화 후 URL이 비어도 과거 state의 ticker가 다시 살아나는 것을 막는다.
    const canConsumeRouteState =
      !q0 &&
      !exp0 &&
      !urlTicker0 &&
      looksLikeTicker(routeStateTicker) &&
      norm(consumedRouteStateTickerRef.current) !== norm(routeStateTicker);

    if (canConsumeRouteState) {
      consumedRouteStateTickerRef.current = routeStateTicker;
    }

    const ticker0 = urlTicker0 || (canConsumeRouteState ? routeStateTicker : '');

    const nextTicker =
      (looksLikeTicker(ticker0) ? ticker0 : '') ||
      (looksLikeTicker(exp0) ? exp0 : '') ||
      (looksLikeTicker(q0) ? q0 : '') ||
      undefined;

    const effectiveQuery = q0 || nextTicker || '';

    const signature = `${q0}__${exp0}__${ticker0}__${nextTicker ?? ''}`;
    if (lastDetailSignatureRef.current === signature) return;
    piChartTrace('route:selection', {
      nextTicker: nextTicker || '', hasQuery: Boolean(q0), hasExpand: Boolean(exp0),
      hasUrlTicker: Boolean(urlTicker0), fromRouteState: Boolean(canConsumeRouteState),
      requestedDate: to,
    });
    lastDetailSignatureRef.current = signature;

    // URL의 정렬/검색/expand 필드만 달라진 경우 동일 종목 데이터를
    // 지우면 안 된다. 재무 fetch effect는 ticker 변화에만 의존하므로,
    // 같은 ticker에서 여기서 history를 비우면 재요청도 일어나지 않는다.
    const isSameTicker = routeSelectedTickerRef.current !== undefined &&
      normalizeTickerInput(routeSelectedTickerRef.current) === normalizeTickerInput(nextTicker ?? '');
    if (isSameTicker) {
      piFinancialBindingLog('route:same_ticker_preserved', { ticker: nextTicker || '', queryChanged: true });
      setNameQuery(effectiveQuery);
      setDebouncedQ(effectiveQuery);
      return;
    }
    routeSelectedTickerRef.current = nextTicker;

    // 외부 종목이 들어온 순간부터 자동 첫 종목 선택은 더 이상 개입하지 않는다.
    if (nextTicker) {
      allowInitialAutoSelectRef.current = false;
    }

    // 이전 종목의 상세 요청은 새 route selection과 경쟁할 수 없게 즉시 무효화한다.
    detailRequestSeqRef.current += 1;
    detailAbortRef.current?.abort();
    detailAbortRef.current = null;

    setNameQuery(effectiveQuery);
    setDebouncedQ(effectiveQuery);
    setPage(1);

    if (nextTicker) {
      if (!q0) setSearchParam('q', nextTicker);
      if (!exp0) setSearchParam('expand', nextTicker);
      setSearchParam('ticker', nextTicker);
    }

    setSelectedTicker(nextTicker);
    setExpandTicker(nextTicker);
    setSelectedDetailRow(undefined);
    setActiveDate(null);
    setStockDetailLoading(false);
    setDetailError('');
    setScreenerFinancial(null);
    setFinancialHistory([]);
    setFinancialHistoryError('');
    setFinancialHistoryBasis('');
    setFinancialHistoryLatestYear('');
    lastFetchedDetailKeyRef.current = '';
    financialDetailRefreshKeyRef.current = '';
  }, [location.search, location.state, setPage, to]);

  useEffect(() => {
    let alive = true;

    async function loadRows() {
      const started = performance.now();
      piChartTrace('list:start', { requestedDate: to, preloadLimit: PRELOAD_LIMIT });
      setRowsReadyForDate(null);
      setLoading(true);
      setErr('');

      try {
        const url = buildListUrl({ to });
        const cacheKey = url;

        if (
          quickBucketListCache &&
          quickBucketListCache.key === cacheKey &&
          Date.now() - quickBucketListCache.savedAt < QUICK_BUCKET_CACHE_TTL_MS
        ) {
          if (!alive) return;
          piChartTrace('list:cache_hit', {
            requestedDate: to, count: quickBucketListCache.rows.length,
            cacheAgeMs: Date.now() - quickBucketListCache.savedAt,
          });
          setRows(quickBucketListCache.rows);
          setRowsReadyForDate(to);
          setLoading(false);
          return;
        }

        const raw = await fetchJSON<any>(url);

        const list =
          Array.isArray(raw)
            ? raw
            : Array.isArray(raw?.content)
              ? raw.content
              : Array.isArray(raw?.items)
                ? raw.items
                : Array.isArray(raw?.rows)
                  ? raw.rows
                  : Array.isArray(raw?.data)
                    ? raw.data
                    : [];

        if (!alive) return;

        const mappingStart = performance.now();
        const mappedRows = list.map(mapRow);
        piChartTrace('list:ready', {
          requestedDate: to, count: mappedRows.length,
          firstObservedDate: mappedRows[0]?.date || '',
          mapMs: piChartElapsed(mappingStart), totalMs: piChartElapsed(started),
        });
        quickBucketListCache = { key: cacheKey, savedAt: Date.now(), rows: mappedRows };
        setRows(mappedRows);
        setRowsReadyForDate(to);
      } catch (error: any) {
        if (!alive) return;

        piChartTrace('list:error', {
          requestedDate: to, elapsedMs: piChartElapsed(started),
          errorName: String(error?.name || 'Error'),
          errorMessage: String(error?.message || 'unknown').slice(0, 80),
        });
        setRows([]);
        setRowsReadyForDate(null); // network failure is not an empty valid snapshot
        setErr(error?.message || '종목 데이터를 불러오지 못했습니다.');
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadRows();

    return () => {
      alive = false;
    };
  }, [to]);

  const clearSelection = useCallback(() => {
    // 초기화/검색 해제 뒤에는 후보 첫 종목을 자동 재선택하지 않는다.
    allowInitialAutoSelectRef.current = false;

    // 이전 상세 응답이 늦게 도착해 현재 선택 상태를 덮어쓰지 못하게 한다.
    detailRequestSeqRef.current += 1;
    detailAbortRef.current?.abort();
    detailAbortRef.current = null;

    canonicalSearchSelectionRef.current = null;
    stockSearchRequestSeqRef.current += 1;

    routeSelectedTickerRef.current = undefined;
    setSelectedTicker(undefined);
    setExpandTicker(undefined);
    setSelectedDetailRow(undefined);
    setActiveDate(null);
    setStockDetailLoading(false);
    setDetailError('');

    lastDetailSignatureRef.current = '';
    lastFetchedDetailKeyRef.current = '';
    financialDetailRefreshKeyRef.current = '';

    setSearchParam('ticker', undefined);
    setSearchParam('expand', undefined);
  }, []);

  const clearSearch = useCallback((closeSelection = false) => {
    setNameQuery('');
    setDebouncedQ('');
    setPage(1);
    setSearchParam('q', undefined);

    if (closeSelection) {
      clearSelection();
    }
  }, [clearSelection, setPage]);

  const focusTicker = useCallback((ticker?: string, expand = true) => {
    if (!ticker) return;

    const cleanTicker = normalizeTickerInput(ticker);
    if (!/^\d{6}$/.test(cleanTicker)) return;
    piChartTrace('selection:focus', { ticker: cleanTicker, expand, previousTicker: selectedTicker || '' });

    // CRITICAL: Autocomplete onSelect followed by Enter/검색, or a second click on
    // the same stock, used to wipe valid annual/screener state. Their effects
    // depend on [selectedTicker], so React did NOT fetch them again. Never
    // clear data for an already selected ticker.
    const previouslySelected = normalizeTickerInput(S(routeSelectedTickerRef.current ?? selectedTicker));
    if (previouslySelected === cleanTicker) {
      allowInitialAutoSelectRef.current = false;
      routeSelectedTickerRef.current = cleanTicker;
      setSelectedTicker(cleanTicker);
      setExpandTicker(expand ? cleanTicker : undefined);
      setDetailTab('overview');
      setCandidateRailOpen(false);
      setSearchParam('ticker', cleanTicker);
      setSearchParam('expand', expand ? cleanTicker : undefined);
      piChartTrace('selection:same_ticker_preserve', {
        ticker: cleanTicker, historyYears: financialHistory.length,
        screenerReady: Boolean(screenerFinancial), detailReady: Boolean(selectedDetailRow),
      });
      // Only explicit repeated searches may retry a failed/empty resource.
      // Bump the request key instead of resetting the old, correct data.
      if (!financialHistoryLoading && (financialHistoryError || financialHistory.length === 0)) {
        setFinancialHistoryRetryToken((current) => current + 1);
      }
      if (!screenerFinancialLoading && (screenerFinancialError || !screenerFinancial)) {
        setScreenerRetryToken((current) => current + 1);
      }
      if (!stockDetailLoading && (detailError || !selectedDetailRow)) {
        lastFetchedDetailKeyRef.current = '';
        setDetailRetryToken((current) => current + 1);
      }
      return;
    }

    allowInitialAutoSelectRef.current = false;

    // A 상세 조회 중 B를 선택하면 A 요청을 즉시 무효화한다.
    detailRequestSeqRef.current += 1;
    detailAbortRef.current?.abort();
    detailAbortRef.current = null;
    lastFetchedDetailKeyRef.current = '';
    financialDetailRefreshKeyRef.current = '';

    // selection authority는 사용자/route 선택만 가진다.
    routeSelectedTickerRef.current = cleanTicker;
    setSelectedTicker(cleanTicker);
    setExpandTicker(expand ? cleanTicker : undefined);
    setDetailTab('overview');
    setCandidateRailOpen(false);
    setSelectedDetailRow(undefined);
    setActiveDate(null);
    setStockDetailLoading(false);
    setDetailError('');

    // 종목 전환 순간 이전 종목의 재무/사업 상태가 남아 보이지 않도록 초기화한다.
    setScreenerFinancial(null);
    setScreenerFinancialError('');
    setScreenerFinancialLoading(false);
    setFinancialHistoryLoading(false);
    setFinancialHistory([]);
    setFinancialHistoryError('');
    setFinancialHistoryBasis('');
    setFinancialHistoryLatestYear('');

    setSearchParam('ticker', cleanTicker);
    setSearchParam('expand', expand ? cleanTicker : undefined);
  }, [selectedTicker, financialHistory, financialHistoryLoading, financialHistoryError, screenerFinancial, screenerFinancialLoading, screenerFinancialError, selectedDetailRow, stockDetailLoading, detailError]);

  const fetchDetailByTicker = useCallback(

    async (ticker: string, resolvedDate?: string) => {
      const cleanTicker = normalizeTickerInput(ticker);
      if (!/^\d{6}$/.test(cleanTicker)) return;

      const listFallback = rows.find((row) => normalizeTickerInput(row.ticker) === cleanTicker);
      const baseDate = resolvedDate || resolveQuickBucketDetailDate(to || today, cleanTicker, rows);

      const params = new URLSearchParams();
      params.set('date', baseDate);
      params.set('ticker', cleanTicker);
      // 사업/재무 profile은 배치 수집 직후에도 갱신될 수 있다.
      // 동일 URL의 브라우저/프록시 stale cache를 재사용하지 않는다.
      params.set('_fresh', String(Date.now()));

      // 상세 데이터 요청은 latest-wins 정책으로 처리한다.
      const started = performance.now();
      const requestSeq = ++detailRequestSeqRef.current;
      piChartTrace('detail:start', {
        requestSeq, ticker: cleanTicker, requestedDate: to,
        resolvedDate: baseDate, fallbackDate: listFallback?.date || '',
        hasListFallback: Boolean(listFallback),
        listFallbackScore: financialScoreOf(listFallback) ?? null,
      });
      detailAbortRef.current?.abort();

      const controller = new AbortController();
      detailAbortRef.current = controller;

      setStockDetailLoading(true);
      setDetailError('');

      try {
        const raw = await fetchJSON<any>(
          `${QUICK_BUCKET_DETAIL_ENDPOINT}?${params.toString()}`,
          controller.signal,
          'no-store',
        );

        if (controller.signal.aborted || requestSeq !== detailRequestSeqRef.current) {
          piChartTrace('detail:dropped', {
            requestSeq, ticker: cleanTicker, reason: controller.signal.aborted ? 'ABORT' : 'STALE_SEQ',
            elapsedMs: piChartElapsed(started),
          });
          return;
        }

        const hasRaw = raw && typeof raw === 'object' && Object.keys(raw).length > 0;
        const responseTicker = normalizeTickerInput(firstText(
          raw?.ticker, raw?.norm_ticker, raw?.stockCode, raw?.stock_code, raw?.code,
        ));
        if (responseTicker && responseTicker !== cleanTicker) {
          piChartTrace('detail:ticker_mismatch', { requestSeq, ticker: cleanTicker, responseTicker });
          throw new Error('상세 API의 종목코드가 요청한 종목과 다릅니다.');
        }

        const mergedRaw = hasRaw
          ? {
              ...(listFallback || {}),
              ...raw,
              financial_score:
                raw?.financial_score ??
                raw?.financialScore ??
                (listFallback as any)?.financialScore ??
                (listFallback as any)?.financial_score,
              financial_grade:
                raw?.financial_grade ??
                raw?.financialGrade ??
                (listFallback as any)?.financialGrade ??
                (listFallback as any)?.financial_grade,
              financial_risk_level:
                raw?.financial_risk_level ??
                raw?.financialRiskLevel ??
                (listFallback as any)?.financialRiskLevel ??
                (listFallback as any)?.financial_risk_level,
              financialProfile:
                raw?.financialProfile ??
                raw?.financial_profile ??
                (listFallback as any)?.financialProfile ??
                null,
              // The nested object may be empty while the flat detail has
              // meaningful business fields. Normalize both before fallback.
              businessProfile:
                normalizeBusinessProfile(raw) ??
                (listFallback as any)?.businessProfile ??
                null,
            }
          : listFallback;

        // 중요: API 응답은 selectedTicker를 변경하지 않는다.
        // 현재 선택 종목의 상세 데이터만 보강한다.
        const normalizedDetail = mergedRaw ? mapRow(mergedRaw) : undefined;
        piChartTrace('detail:apply', {
          requestSeq, ticker: cleanTicker, elapsedMs: piChartElapsed(started),
          responseEmpty: !hasRaw, responseTicker: responseTicker || '',
          responseScore: n(raw?.financial_score ?? raw?.financialScore) ?? null,
          listFallbackScore: financialScoreOf(listFallback) ?? null,
          selectedScore: financialScoreOf(normalizedDetail) ?? null,
          hasFinancialProfile: Boolean(normalizedDetail?.financialProfile),
          hasBusinessProfile: Boolean(normalizedDetail?.businessProfile),
          productCount: businessProductTerms(normalizedDetail?.businessProfile).length,
          technologyCount: businessTechnologyTerms(normalizedDetail?.businessProfile).length,
        });
        setSelectedDetailRow(normalizedDetail);
      } catch (error: any) {
        if (
          controller.signal.aborted ||
          requestSeq !== detailRequestSeqRef.current ||
          error?.name === 'AbortError'
        ) {
          return;
        }

        piChartTrace('detail:error', {
          requestSeq, ticker: cleanTicker, resolvedDate: baseDate,
          elapsedMs: piChartElapsed(started), errorName: String(error?.name || 'Error'),
          errorMessage: String(error?.message || 'unknown').slice(0, 80),
        });
        setSelectedDetailRow(listFallback ? mapRow(listFallback) : undefined);
        setDetailError(error?.message ?? 'detail failed');
      } finally {
        if (requestSeq === detailRequestSeqRef.current) {
          if (detailAbortRef.current === controller) {
            detailAbortRef.current = null;
          }
          piChartTrace('detail:settle', { requestSeq, ticker: cleanTicker, elapsedMs: piChartElapsed(started) });
          setStockDetailLoading(false);
        }
      }
    },
    [to, rows],
  );

  useEffect(() => {
    const ticker = normalizeTickerInput(S(expandTicker || selectedTicker));
    if (!/^\d{6}$/.test(ticker)) return;
    // Critical first-load race: on a cold navigation, the selected ticker
    // arrives before the quick-bucket list. The initial detail request would
    // use Sunday/today; its memo key then prevented a corrected KRX date fetch.
    if (rowsReadyForDate !== to || loading) {
      piChartTrace('detail:gate_wait', {
        ticker, requestedDate: to, readyDate: rowsReadyForDate || '',
        listLoading: loading, listCount: rows.length,
      });
      return;
    }

    const snapshotDate = resolveQuickBucketDetailDate(to || today, ticker, rows);
    const detailKey = `${to}__${snapshotDate}__${ticker}`;
    if (lastFetchedDetailKeyRef.current === detailKey) {
      piChartTrace('detail:dedupe_skip', { ticker, requestedDate: to, snapshotDate });
      return;
    }

    piChartTrace('detail:effect_dispatch', { ticker, requestedDate: to, snapshotDate });
    lastFetchedDetailKeyRef.current = detailKey;
    void fetchDetailByTicker(ticker, snapshotDate);
  }, [expandTicker, selectedTicker, to, rows, rowsReadyForDate, loading, fetchDetailByTicker, detailRetryToken]);

  useEffect(() => {
    if (detailTab !== 'financial') return;

    const ticker = S(selectedTicker).trim();
    if (!/^\d{6}$/.test(ticker)) return;
    if (rowsReadyForDate !== to || loading) return;
    if (lastFetchedDetailKeyRef.current === '') return;

    // 검색 직후 상세 응답이 빈 financial/business profile을 들고 왔더라도
    // 재무 탭 진입 시 새로고침 없이 authoritative detail을 한 번 더 확인한다.
    const hasFinancialProfile = Boolean(selectedDetailRow?.financialProfile);
    const hasBusinessProfile = Boolean(selectedDetailRow?.businessProfile);

    if (hasFinancialProfile && hasBusinessProfile) return;
    // Never schedule another request while the first detail is in flight.
    if (stockDetailLoading) return;

    const refreshKey = `${to || today}__${norm(ticker)}__financial`;
    if (financialDetailRefreshKeyRef.current === refreshKey) return;

    financialDetailRefreshKeyRef.current = refreshKey;
    piChartTrace('detail:financial_fallback_dispatch', { ticker, refreshKey });
    void fetchDetailByTicker(ticker, resolveQuickBucketDetailDate(to || today, ticker, rows));
  }, [detailTab, selectedTicker, to, fetchDetailByTicker, selectedDetailRow?.financialProfile, selectedDetailRow?.businessProfile, stockDetailLoading, rowsReadyForDate, loading, rows]);

  const filteredRows = useMemo(() => {
    return applyClientFilters(rows, {
      query: debouncedQ,
      marketFilter,
    });
  }, [rows, debouncedQ, marketFilter]);

  const sortedRows = useMemo(() => {
    return clientSortRows(filteredRows, sort, order);
  }, [filteredRows, sort, order]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE));
  }, [sortedRows.length]);

  const safePage = Math.min(Math.max(page, 1), totalPages);

  const pagedRows = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return sortedRows.slice(start, end);
  }, [sortedRows, safePage]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages, setPage]);

  const selectedRow = useMemo(() => {
    if (!selectedTicker) return undefined;
    if (selectedDetailRow && normalizeTickerInput(S(selectedDetailRow.ticker)) === normalizeTickerInput(selectedTicker)) {
      return selectedDetailRow;
    }
    return rows.find((row) => norm(row.ticker) === norm(selectedTicker));
  }, [selectedDetailRow, selectedTicker, rows]);

  const lastRenderDiagnosticRef = useRef('');
  useEffect(() => {
    if (!piChartTracingEnabled()) return;
    const snapshot = {
      ticker: selectedTicker || '', tab: detailTab,
      selectedRowTicker: selectedRow?.ticker || '',
      rowAuthority: selectedDetailRow ? 'detail' : selectedRow ? 'list' : 'none',
      score: financialScoreOf(selectedRow) ?? null,
      financialProfile: Boolean(selectedRow?.financialProfile),
      businessProfile: Boolean(selectedRow?.businessProfile),
      screenerMatch: Boolean(screenerFinancial),
      historyYears: financialHistory.length,
      detailLoading: stockDetailLoading, screenerLoading: screenerFinancialLoading,
      historyLoading: financialHistoryLoading,
      detailErrorPresent: Boolean(detailError),
      historyErrorPresent: Boolean(financialHistoryError),
    };
    const signature = JSON.stringify(snapshot);
    if (signature === lastRenderDiagnosticRef.current) return;
    lastRenderDiagnosticRef.current = signature;
    piChartTrace('ui:committed', snapshot);
  }, [selectedTicker, detailTab, selectedRow, selectedDetailRow, screenerFinancial,
    financialHistory, stockDetailLoading, screenerFinancialLoading, financialHistoryLoading,
    detailError, financialHistoryError]);

  useEffect(() => {
    const ticker = normalizeTickerInput(S(selectedTicker));
    if (!/^\d{6}$/.test(ticker)) {
      setScreenerFinancial(null);
      setScreenerFinancialError('');
      setScreenerFinancialLoading(false);
      return;
    }

    let cancelled = false;
    const started = performance.now();
    piChartTrace('screener:start', { ticker });
    setScreenerFinancialLoading(true);
    setScreenerFinancialError('');

    void searchProfessionalScreener({
      keyword: ticker,
      market: 'ALL',
      excludeRiskAvoid: false,
      page: 0,
      size: 20, // score-ranked matches require an exact ticker check
      sortBy: 'score',
      sortDirection: 'DESC',
    })
      .then((response) => {
        if (cancelled) return;
        // The screener is score-sorted; its first result is not necessarily
        // the requested ticker. Never display another company's fundamentals.
        const items = Array.isArray(response?.items) ? response.items : [];
        const item = items.find((candidate: any) => normalizeTickerInput(firstText(
          candidate?.ticker, candidate?.code, candidate?.stockCode, candidate?.stock_code, candidate?.norm_ticker,
        )) === ticker) ?? null;
        piChartTrace('screener:result', {
          ticker, elapsedMs: piChartElapsed(started), itemCount: items.length,
          exactMatch: Boolean(item), financialScore: financialNumber(item, 'financialScore')
            ?? financialNumber(item, 'financial_score') ?? null,
          financialBaseYear: String(financialValue(item, 'fy0Year') || ''),
          financialCashFlowPresent: financialValue(item, 'fcfYieldPct') != null,
        });
        setScreenerFinancial(item);
        if (!item) setScreenerFinancialError('해당 종목과 일치하는 재무 스크리너 데이터가 없습니다.');
      })
      .catch((error: any) => {
        if (cancelled) return;
        piChartTrace('screener:error', {
          ticker, elapsedMs: piChartElapsed(started),
          errorName: String(error?.name || 'Error'),
        });
        setScreenerFinancial(null);
        setScreenerFinancialError('재무 스크리너 스냅샷을 불러오지 못했습니다.');
      })
      .finally(() => {
        if (!cancelled) setScreenerFinancialLoading(false);
      });

    return () => { cancelled = true; };
  }, [selectedTicker, screenerRetryToken]);

  useEffect(() => {
    // Start on stock selection, not on financial-tab activation.
    // Switching tabs no longer clears/re-fetches a successful annual series.
    const ticker = normalizeTickerInput(S(selectedTicker));
    if (!/^\d{6}$/.test(ticker)) {
      setFinancialHistory([]);
      setFinancialHistoryError('');
      setFinancialHistoryBasis('');
      setFinancialHistoryLatestYear('');
      setFinancialHistoryLoading(false);
      return;
    }

    let cancelled = false;
    const started = performance.now();
    piChartTrace('history:start', { ticker, years: 5 });
    setFinancialHistoryLoading(true);
    setFinancialHistoryError('');

    void getProfessionalFinancialHistory(ticker, 5)
      .then((response) => {
        if (cancelled) return;
        const normalized = normalizeFinancialHistoryResponse(response, ticker);
        piFinancialBindingLog('history:normalized', {
          ticker, envelope: normalized.envelope, years: normalized.items.map(item => item.year),
          availableRevenueYears: normalized.items.filter(item => item.revenue != null).length,
        });
        piChartTrace('history:result', {
          ticker, elapsedMs: piChartElapsed(started), itemCount: normalized.items.length,
          envelope: normalized.envelope, firstYear: normalized.items[0]?.year || '',
          latestYear: normalized.latestYear,
          hasRevenue: normalized.items.some(item => item?.revenue != null),
          hasOperatingIncome: normalized.items.some(item => item?.operatingIncome != null),
          basisPresent: Boolean(normalized.basis),
        });
        setFinancialHistory(normalized.items);
        setFinancialHistoryBasis(normalized.basis);
        setFinancialHistoryLatestYear(normalized.latestYear);
        if (!normalized.items.length) {
          setFinancialHistoryError(normalized.found
            ? '연간 재무 원천이 정상 응답했지만 표시 가능한 연도 자료가 없습니다.'
            : '등록된 연간 재무 자료가 없습니다.');
        }
      })
      .catch((error: any) => {
        if (cancelled) return;
        piChartTrace('history:error', {
          ticker, elapsedMs: piChartElapsed(started),
          errorName: String(error?.name || 'Error'),
        });
        setFinancialHistory([]);
        setFinancialHistoryBasis('');
        setFinancialHistoryLatestYear('');
        setFinancialHistoryError('연간 재무 API 응답을 화면 데이터로 연결하지 못했습니다. 응답 형태와 종목 일치 여부를 확인하세요.');
      })
      .finally(() => {
        if (!cancelled) setFinancialHistoryLoading(false);
      });

    return () => { cancelled = true; };
  }, [selectedTicker, financialHistoryRetryToken]);

  useEffect(() => {
    piFinancialBindingLog('history:committed', {
      ticker: selectedTicker || '', count: financialHistory.length,
      years: financialHistory.map(item => item.year), loading: financialHistoryLoading,
      hasError: Boolean(financialHistoryError),
    });
  }, [selectedTicker, financialHistory, financialHistoryLoading, financialHistoryError]);

  useEffect(() => {
    if (!allowInitialAutoSelectRef.current) return;
    if (loading || selectedTicker || pagedRows.length === 0) return;

    // 첫 진입의 편의 기능으로만 1회 자동 선택한다.
    // 초기화 이후에는 다시 개입하지 않는다.
    allowInitialAutoSelectRef.current = false;
    focusTicker(pagedRows[0].ticker, false);
  }, [loading, selectedTicker, pagedRows, focusTicker]);

  const kpi = useMemo(() => {
    const total = filteredRows.length;
    const action = filteredRows.filter((row) => inferDecisionBucket(row) === 'Action').length;
    const review = filteredRows.filter((row) => inferDecisionBucket(row) === 'Review').length;
    const risk = filteredRows.filter((row) => inferDecisionBucket(row) === 'Risk').length;
    const directPolicy = filteredRows.filter((row) => row.hasDirectPolicy || (row.directCount7d ?? 0) > 0).length;
    const financialRisk = filteredRows.filter((row) => (financialScoreOf(row) ?? 100) < 50 || financialRiskOf(row).includes('높')).length;

    return {
      total,
      action,
      review,
      risk,
      directPolicy,
      financialRisk,
    };
  }, [filteredRows]);

  const marketSummary = useMemo(() => {
    if (filteredRows.length === 0) {
      return '현재 조건에 맞는 종목이 없습니다. 검색어 또는 필터를 조정하세요.';
    }

    if (kpi.risk > kpi.action && kpi.risk >= 3) {
      return '진입 경계 종목 비중이 높습니다. 신규 관찰보다 과열·재무·수급 리스크 확인을 우선하세요.';
    }

    if (kpi.action >= 3) {
      return '우선 확인 후보가 확보되었습니다. 가격 추격보다 거래대금과 외국인·기관 수급 지속성을 확인하세요.';
    }

    if (kpi.directPolicy >= 3) {
      return '직접 정책 연결 종목이 여럿 감지되었습니다. 이슈가 실제 수급과 가격 반응으로 이어지는지 확인하세요.';
    }

    return '정책 연결, 가격 반응, 거래대금, 재무 리스크를 함께 확인해야 하는 혼재 구간입니다.';
  }, [filteredRows.length, kpi]);

  const applyResolvedStockSearch = (stockSuggestion: StockSearchSuggestion) => {
    const ticker = normalizeTickerInput(S(stockSuggestion?.ticker));
    if (!/^\d{6}$/.test(ticker)) return false;

    const displayName = normalizeStockSearchText(S(stockSuggestion?.corpName)) || ticker;
    canonicalSearchSelectionRef.current = { ticker, displayName };

    setNameQuery(displayName);
    setDebouncedQ(ticker);
    setPage(1);
    setSearchParam('q', displayName);
    focusTicker(ticker, true);
    return true;
  };

  const handleSearchSubmit = async () => {
    const clean = normalizeStockSearchText(nameQuery);

    setPage(1);
    setSearchParam('q', clean || undefined);

    if (!clean) {
      clearSelection();
      return;
    }

    const canonicalSelected = canonicalSearchSelectionRef.current;
    if (
      canonicalSelected &&
      stockNameKey(canonicalSelected.displayName) === stockNameKey(clean)
    ) {
      setDebouncedQ(canonicalSelected.ticker);
      focusTicker(canonicalSelected.ticker, true);
      return;
    }

    if (looksLikeTicker(clean)) {
      const ticker = normalizeTickerInput(clean);
      canonicalSearchSelectionRef.current = { ticker, displayName: ticker };
      setNameQuery(ticker);
      setDebouncedQ(ticker);
      setSearchParam('q', ticker);
      focusTicker(ticker, true);
      return;
    }

    // Search authority is /api/stocks/search. The quick-bucket list is a chart/list
    // cache and must not decide whether a company name exists.
    const requestSeq = ++stockSearchRequestSeqRef.current;

    try {
      const suggestions = await resolveStockSuggestions(clean, 10);
      if (requestSeq !== stockSearchRequestSeqRef.current) return;

      const queryKey = stockNameKey(clean);
      const exact = suggestions.find(
        (item) => stockNameKey(S(item?.corpName)) === queryKey,
      );
      const resolved = exact || (suggestions.length === 1 ? suggestions[0] : undefined);

      if (resolved && applyResolvedStockSearch(resolved)) return;
    } catch {
      // Keep a local fallback for temporary search-API failure. It is not the
      // primary authority and is intentionally limited to exact/single candidates.
    }

    if (requestSeq !== stockSearchRequestSeqRef.current) return;

    const exactName = rows.find(
      (row) => stockNameKey(row.corp_name) === stockNameKey(clean),
    );
    const candidates = rows.filter(
      (row) =>
        contains(row.corp_name, clean) ||
        contains(row.ticker, clean) ||
        contains(row.reportSector, clean),
    );
    const resolved = exactName || (candidates.length === 1 ? candidates[0] : undefined);

    if (resolved?.ticker) {
      applyResolvedStockSearch({
        ticker: S(resolved.ticker),
        corpName: S(resolved.corp_name),
      } as StockSearchSuggestion);
      return;
    }

    // No authoritative single stock was resolved. Keep the typed text as a list
    // filter instead of fabricating a ticker selection.
    canonicalSearchSelectionRef.current = null;
    setDebouncedQ(clean);
  };

  const handleSelectStockSuggestion = (stockSuggestion: StockSearchSuggestion) => {
    stockSearchRequestSeqRef.current += 1;
    applyResolvedStockSearch(stockSuggestion);
  };


  const handleExpandedTickerChange = (ticker: string | null) => {
    if (!ticker) {
      setExpandTicker(undefined);
      setActiveDate(null);
      setSearchParam('expand', undefined);
      return;
    }

    focusTicker(ticker, true);
  };

  const resetAll = () => {
    clearSearch(true);
    setFrom(defaultFrom);
    setTo(today);
    setSort('value');
    setOrder('desc');
    setView('candles');
    setMarketFilter('ALL');
    setPage(1);
  };

  const selectedChange = selectedRow?.price_change_percentage ?? 0;
  const selectedTone: Tone = selectedChange > 0 ? 'red' : selectedChange < 0 ? 'blue' : 'slate';
  const selectedDecision = selectedRow ? inferDecisionBucket(selectedRow) : null;
  const selectedDecisionTone = selectedDecision ? decisionTone(selectedDecision) : 'slate';
  const selectedFlowLabel = selectedRow ? flowLabel(selectedRow) : '-';
  const selectedForeignZ = selectedRow?.foreignZ;

  const detailTabs: Array<{ key: StockDetailTab; label: string }> = [
    { key: 'overview', label: '개요' },
    { key: 'chart', label: '차트' },
    { key: 'financial', label: '재무' },
    { key: 'issues', label: '이슈·공시' },
  ];

  return (
    <>
      <SEO
        title="국내주식 종목 인텔리전스 | 판단·차트·재무·이슈 | 폴리시인텔"
        description="국내주식의 현재 판단과 변화 조건, 가격·수급 차트, 재무, 이슈·공시를 한 종목 화면에서 확인합니다."
        path="/stocks"
      />

      <div className="pi-intel-page">
        <div className="pi-intel-shell">
          <section className="pi-intel-searchbar" aria-label="종목 검색">
            <div className="pi-intel-search-input">
              <StockSearchSuggestInput
                value={nameQuery}
                onChange={(value) => {
                  const cleanValue = normalizeStockSearchText(value);
                  const selected = canonicalSearchSelectionRef.current;
                  if (
                    selected &&
                    stockNameKey(cleanValue) !== stockNameKey(selected.displayName)
                  ) {
                    canonicalSearchSelectionRef.current = null;
                    stockSearchRequestSeqRef.current += 1;
                  }

                  setNameQuery(value);
                  if (cleanValue === '') {
                    setDebouncedQ('');
                    setPage(1);
                    setSearchParam('q', undefined);
                    clearSelection();
                  }
                }}
                onSelect={handleSelectStockSuggestion}
                onEnter={handleSearchSubmit}
                selectedValueMode="name"
                selectFirstOnEnter
                minLength={1}
                limit={10}
                placeholder="종목명 또는 종목코드 검색"
                inputSx={{
                  '& .MuiOutlinedInput-root': {
                    minHeight: 46,
                    borderRadius: '10px',
                    background: '#fff',
                    '& fieldset': { borderColor: '#d9dde5' },
                    '&:hover fieldset': { borderColor: '#b8bec9' },
                    '&.Mui-focused fieldset': { borderColor: '#2962ff', borderWidth: 1 },
                  },
                  '& .MuiInputBase-input': {
                    fontSize: 13,
                    fontWeight: 750,
                  },
                }}
              />
            </div>

            <div className="pi-intel-search-actions">
              <button type="button" className="pi-intel-btn is-primary" onClick={handleSearchSubmit}>검색</button>
              <button
                type="button"
                className={`pi-intel-btn ${mobileFiltersOpen ? 'is-active' : ''}`}
                onClick={() => setMobileFiltersOpen((value) => !value)}
              >
                조건
              </button>
              <button
                type="button"
                className={`pi-intel-btn ${candidateRailOpen ? 'is-active' : ''}`}
                onClick={() => setCandidateRailOpen((value) => !value)}
              >
                후보 {filteredRows.length}
              </button>
              {!isMobile ? (
                <button type="button" className="pi-intel-btn is-ghost" onClick={resetAll}>초기화</button>
              ) : null}
            </div>
          </section>

          {mobileFiltersOpen ? (
            <section className="pi-intel-filter-strip">
              <label>
                <span>기준일</span>
                <input type="date" value={to} onChange={(event) => setTo(event.target.value)} />
              </label>
              <label>
                <span>정렬</span>
                <select value={sort} onChange={(event) => setSort(event.target.value as SortKey)}>
                  <option value="decision">판단</option>
                  <option value="value">거래대금</option>
                  <option value="volume">거래량</option>
                  <option value="change">등락률</option>
                  <option value="policy">정책 연결</option>
                  <option value="financial">재무</option>
                  <option value="close">종가</option>
                </select>
              </label>
              <label>
                <span>순서</span>
                <select value={order} onChange={(event) => setOrder(event.target.value as SortOrder)}>
                  <option value="desc">내림차순</option>
                  <option value="asc">오름차순</option>
                </select>
              </label>
              <label>
                <span>시장</span>
                <select
                  value={marketFilter}
                  onChange={(event) => {
                    setMarketFilter(event.target.value as StockMarketFilter);
                    setPage(1);
                  }}
                >
                  <option value="ALL">전체</option>
                  <option value="KOSPI">코스피</option>
                  <option value="KOSDAQ">코스닥</option>
                  <option value="ETF">ETF</option>
                  <option value="ETN">ETN</option>
                  <option value="KONEX">코넥스</option>
                  <option value="OTHER">기타</option>
                </select>
              </label>
              {isMobile ? (
                <button type="button" className="pi-intel-filter-reset" onClick={resetAll}>조건 초기화</button>
              ) : null}
            </section>
          ) : null}

          {err ? <div className="pi-intel-error">{err}</div> : null}

          {(!selectedRow || candidateRailOpen) ? (
            <section className="pi-intel-candidate-panel">
              <div className="pi-intel-section-head is-compact">
                <div>
                  <span className="pi-intel-kicker">CANDIDATES</span>
                  <strong>비교 후보</strong>
                  <small>{loading ? '업데이트 중' : `${filteredRows.length}개 · ${safePage}/${totalPages} 페이지`}</small>
                </div>
                <div className="pi-intel-section-actions">
                  {totalPages > 1 ? (
                    <>
                      <button
                        type="button"
                        className="pi-intel-icon-btn"
                        aria-label="이전 후보 페이지"
                        disabled={safePage <= 1}
                        onClick={() => setPage(Math.max(1, safePage - 1))}
                      >
                        ‹
                      </button>
                      <button
                        type="button"
                        className="pi-intel-icon-btn"
                        aria-label="다음 후보 페이지"
                        disabled={safePage >= totalPages}
                        onClick={() => setPage(Math.min(totalPages, safePage + 1))}
                      >
                        ›
                      </button>
                    </>
                  ) : null}
                  <button
                    type="button"
                    className="pi-intel-text-btn"
                    onClick={() => setShowFullTable((value) => !value)}
                  >
                    {showFullTable ? '비교표 닫기' : '전체 비교'}
                  </button>
                </div>
              </div>

              <WorkspaceNavigator
                rows={pagedRows}
                selectedTicker={selectedTicker}
                mobile
                onSelect={(ticker) => {
                  focusTicker(ticker, false);
                  setDetailTab('overview');
                }}
              />
            </section>
          ) : null}

          {selectedRow ? (
            <main className="pi-intel-stock-card">
              <header className="pi-intel-stock-hero">
                <div className="pi-intel-stock-identity">
                  <div className="pi-intel-company-line">
                    <h1>{selectedRow.corp_name}</h1>
                    <span>{selectedRow.ticker}{selectedRow.market ? ` · ${selectedRow.market}` : ''}</span>
                  </div>
                  <div className="pi-intel-price-line">
                    <strong>{formatNumber(selectedRow.close)}원</strong>
                    <em className={selectedChange > 0 ? 'is-up' : selectedChange < 0 ? 'is-down' : ''}>
                      {formatPct(selectedChange)}
                    </em>
                  </div>
                  <p>{oneLineConclusion(selectedRow)}</p>
                </div>

                <div className="pi-intel-stock-status">
                  <div className="pi-intel-status-head">
                    <span>현재 판단</span>
                    {selectedDecision ? <Badge tone={selectedDecisionTone}>{decisionLabelKo(selectedDecision)}</Badge> : null}
                  </div>
                  <strong className="pi-intel-score">{finalScore(selectedRow)}</strong>
                  <span className="pi-intel-score-caption">PolicyIntel 종합 점수</span>

                  <div className="pi-intel-signal-grid">
                    {[
                      ['가격', `${trendLabel(selectedRow)} ${trendArrow(selectedRow)}`],
                      ['수급', `${selectedFlowLabel} ${flowArrow(selectedRow)}`],
                      ['재무', financialTone(selectedRow).label],
                      ['가치', valuationCandidateLabel(selectedRow)],
                    ].map(([label, value]) => (
                      <div key={label}>
                        <span>{label}</span>
                        <strong>{value}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              </header>

              <nav className="pi-intel-tabs" aria-label="종목 상세 메뉴">
                {detailTabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    aria-pressed={detailTab === tab.key}
                    className={detailTab === tab.key ? 'is-active' : ''}
                    onClick={() => setDetailTab(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>

              <section className="pi-intel-tab-body">
                {detailTab === 'overview' ? (
                  <div className="pi-intel-overview">
                    <section className="pi-intel-decision-card">
                      <div className="pi-intel-decision-main">
                        <span className="pi-intel-kicker">CURRENT VIEW</span>
                        <div className="pi-intel-decision-title">
                          <strong style={{ color: toneColor(selectedDecisionTone) }}>
                            {selectedDecision ? decisionLabelKo(selectedDecision) : '판단 확인'}
                          </strong>
                          <span>종합 {finalScore(selectedRow)}</span>
                        </div>
                        <p>{oneLineConclusion(selectedRow)}</p>
                      </div>

                      <div className="pi-intel-condition-grid">
                        {[
                          ['유지', selectedRow.judgmentMaintainPrice != null ? `${formatNumber(selectedRow.judgmentMaintainPrice)}원 이상` : '기준 확인 중', ''],
                          ['개선', selectedRow.judgmentImprovePrice != null ? `${formatNumber(selectedRow.judgmentImprovePrice)}원 확인` : '조건 확인 중', 'is-positive'],
                          ['위험', selectedRow.judgmentRiskPrice != null ? `${formatNumber(selectedRow.judgmentRiskPrice)}원 이탈` : '조건 확인 중', 'is-negative'],
                        ].map(([label, value, cls]) => (
                          <div key={label} className={cls}>
                            <span>{label}</span>
                            <strong>{value}</strong>
                          </div>
                        ))}
                      </div>
                    </section>

                    <section className="pi-intel-stat-section">
                      <div className="pi-intel-section-head">
                        <div>
                          <span className="pi-intel-kicker">KEY STATS</span>
                          <strong>핵심 숫자</strong>
                        </div>
                        <small>비교에 필요한 숫자만 남겼습니다.</small>
                      </div>

                      <div className="pi-intel-stat-grid">
                        {[
                          ['시가총액', compactMoney(selectedRow.marketCap)],
                          ['거래대금', compactMoney(selectedRow.tradingValue)],
                          ['PER', selectedRow.per != null ? Number(selectedRow.per).toFixed(2) : '-'],
                          ['PBR', selectedRow.pbr != null ? Number(selectedRow.pbr).toFixed(2) : '-'],
                          ['재무 점수', financialScoreOf(selectedRow) != null ? Number(financialScoreOf(selectedRow)).toFixed(1) : '-'],
                          ['최근 이슈', `${Math.max(0, Number(selectedRow.dailyIssueCount ?? selectedRow.policyCount7d ?? 0) || 0)}건`],
                        ].map(([label, value]) => (
                          <div key={label}>
                            <span>{label}</span>
                            <strong>{value}</strong>
                          </div>
                        ))}
                      </div>
                    </section>

                    <div className="pi-intel-overview-grid">
                      <WorkspaceInsightPanel
                        row={selectedRow}
                        loading={stockDetailLoading}
                        error={detailError}
                        compact={isMobile}
                      />

                      <section className="pi-intel-shortcuts">
                        <div className="pi-intel-section-head is-compact">
                          <div>
                            <span className="pi-intel-kicker">NEXT CHECK</span>
                            <strong>더 확인하기</strong>
                          </div>
                        </div>

                        <button type="button" onClick={() => setDetailTab('chart')}>
                          <span>차트</span>
                          <strong>{trendLabel(selectedRow)} · {selectedFlowLabel}</strong>
                          <small>가격·수급·기술 흐름과 판단 변화</small>
                        </button>
                        <button type="button" onClick={() => setDetailTab('financial')}>
                          <span>재무</span>
                          <strong>{financialTone(selectedRow).label}</strong>
                          <small>{financialSummaryOf(selectedRow)}</small>
                        </button>
                        <button type="button" onClick={() => setDetailTab('issues')}>
                          <span>이슈·공시</span>
                          <strong>{firstText(selectedRow.insightTitle) || '최근 연결 이슈 확인'}</strong>
                          <small>{firstText(selectedRow.insightSummary) || '판단에 연결되는 이슈와 공시를 확인합니다.'}</small>
                        </button>
                      </section>
                    </div>

                    <div className="pi-intel-business-wrap">
                      <BusinessProfileTable row={selectedRow} />
                    </div>
                  </div>
                ) : null}

                {detailTab === 'chart' ? (
                  <div className="pi-intel-chart-tab">
                    <div className="pi-intel-tab-heading">
                      <div>
                        <span className="pi-intel-kicker">PRICE · FLOW · TECHNICAL</span>
                        <h2>차트</h2>
                      </div>
                      <p>가격·거래대금·수급·기술 지표와 판단 변화를 한 화면에서 확인합니다.</p>
                    </div>

                    <div className="pi-intel-technical-strip">
                      {[
                        ['추세', trendLabel(selectedRow), selectedTone],
                        ['20일 이격', selectedRow.maGap20Pct != null ? formatPct(selectedRow.maGap20Pct) : '-', selectedRow.maGap20Pct != null && selectedRow.maGap20Pct > 0 ? 'green' : selectedRow.maGap20Pct != null && selectedRow.maGap20Pct < 0 ? 'red' : 'slate'],
                        ['60일 이격', selectedRow.maGap60Pct != null ? formatPct(selectedRow.maGap60Pct) : '-', selectedRow.maGap60Pct != null && selectedRow.maGap60Pct > 0 ? 'green' : selectedRow.maGap60Pct != null && selectedRow.maGap60Pct < 0 ? 'red' : 'slate'],
                        ['상대강도', selectedRow.rsLocalPct != null ? formatPct(selectedRow.rsLocalPct) : '-', selectedRow.rsLocalPct != null && selectedRow.rsLocalPct > 0 ? 'green' : selectedRow.rsLocalPct != null && selectedRow.rsLocalPct < 0 ? 'red' : 'slate'],
                        ['외국인 강도', selectedForeignZ == null ? '-' : Number(selectedForeignZ).toFixed(2), selectedForeignZ != null && selectedForeignZ > 0 ? 'green' : selectedForeignZ != null && selectedForeignZ < 0 ? 'red' : 'slate'],
                        ['수급', selectedFlowLabel, flowLabel(selectedRow).includes('유입') || flowLabel(selectedRow).includes('강세') ? 'green' : flowLabel(selectedRow).includes('매도') ? 'red' : 'slate'],
                      ].map(([label, value, tone]) => (
                        <div key={label}>
                          <span>{label}</span>
                          <strong style={{ color: tone === 'green' ? UI.green : tone === 'red' ? UI.red : tone === 'blue' ? UI.blue : UI.text }}>
                            {value}
                          </strong>
                        </div>
                      ))}
                    </div>

                    <div className="pi-intel-chart-wrap">
                      <MarketSeriesChart
                        ticker={selectedRow.ticker}
                        view={view}
                        title=""
                        height={isMobile ? 420 : isCompactDesktop ? 540 : 610}
                        indicators={indicators}
                        onIndicatorsChange={(next) => setIndicators((current) => ({ ...current, ...next }))}
                        activeDate={activeDate}
                        onActiveDateChange={(date) => setActiveDate(date)}
                        defaultRange="90D"
                        showDetailPanel={false}
                        showRelatedIssues
                        decisionLabel={selectedDecision ? decisionLabelKo(selectedDecision) : ''}
                        decisionSummary={oneLineConclusion(selectedRow)}
                        decisionLines={decisionPriceLines(selectedRow)}
                        showDecisionMarkers
                      />
                    </div>

                    <div className="pi-intel-footnote">
                      기술 지표는 차트를 읽기 위한 보조 정보입니다. 가격·수급과 재무·가치 판단은 서로의 authority를 침범하지 않습니다.
                    </div>
                  </div>
                ) : null}

                {detailTab === 'financial' ? (
                  <div className="pi-intel-financial-tab">
                    <div className="pi-intel-tab-heading">
                      <div>
                        <span className="pi-intel-kicker">FINANCIALS</span>
                        <h2>재무</h2>
                      </div>
                      <p>사업보고서 기반 연간 실적과 재무 건전성을 확인합니다.</p>
                    </div>

                    <BusinessFinancialPanel
                      row={selectedRow}
                      loading={stockDetailLoading}
                      error={detailError}
                      compact={isMobile}
                      mode="full"
                      screenerFinancial={screenerFinancial}
                      screenerLoading={screenerFinancialLoading}
                      screenerError={screenerFinancialError}
                      financialHistory={financialHistory}
                      financialHistoryLoading={financialHistoryLoading}
                      financialHistoryError={financialHistoryError}
                      financialHistoryBasis={financialHistoryBasis}
                      financialHistoryLatestYear={financialHistoryLatestYear}
                    />
                  </div>
                ) : null}

                {detailTab === 'issues' ? (
                  <div className="pi-intel-issues-tab">
                    <div className="pi-intel-tab-heading">
                      <div>
                        <span className="pi-intel-kicker">ISSUES & DISCLOSURE</span>
                        <h2>이슈·공시</h2>
                      </div>
                      <p>종목 판단에 직접 연결된 이슈와 공시 해석을 모아 봅니다.</p>
                    </div>
                    <StockIssuePanel row={selectedRow} />
                  </div>
                ) : null}
              </section>
            </main>
          ) : (
            <section className="pi-intel-empty">
              <span className="pi-intel-kicker">STOCK INTELLIGENCE</span>
              <strong>종목을 검색하거나 후보에서 선택하세요.</strong>
              <p>현재 판단, 변화 조건, 차트, 재무와 이슈를 한 종목 화면에서 확인합니다.</p>
            </section>
          )}

          {showFullTable ? (
            <section className="pi-intel-comparison-drawer">
              <div className="pi-intel-section-head">
                <div>
                  <span className="pi-intel-kicker">COMPARE</span>
                  <strong>후보 전체 비교</strong>
                  <small>필요할 때만 펼쳐서 비교합니다.</small>
                </div>
                <button type="button" className="pi-intel-text-btn" onClick={() => setShowFullTable(false)}>닫기</button>
              </div>
              <StockRankTable
                chartDefaultRange="3M"
                rows={pagedRows}
                from={from}
                to={to}
                view={view}
                indicators={indicators}
                onIndicatorsChange={(next) => setIndicators(next)}
                expandedTicker={expandTicker ?? null}
                onExpandedTickerChange={handleExpandedTickerChange}
                activeDate={activeDate}
                onActiveDateChange={(date) => setActiveDate(date ?? null)}
                selectedTicker={selectedTicker ?? null}
              />
            </section>
          ) : null}

          <div className="pi-intel-market-summary">{marketSummary}</div>
        </div>
      </div>
    </>
  );
};

const styles: Record<string, React.CSSProperties> = {
  stockPage: {
      minHeight: 'auto',
      background: 'transparent',
      color: UI.text,
    },
  stockShell: {
      width: '100%',
      maxWidth: 'none',
      margin: '0 auto',
      boxSizing: 'border-box',
    },
  stockSearchBar: {
      position: 'sticky',
      top: 6,
      zIndex: 30,
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: 8,
      marginBottom: 8,
      border: `1px solid ${UI.border}`,
      borderRadius: 7,
      background: 'var(--pi-surface-subtle)',
      backdropFilter: 'none',
      boxShadow: 'none',
    },
  stockSearchBarMobile: {
      position: 'static',
      alignItems: 'stretch',
    },
  stockSearchInput: {
      flex: '1 1 auto',
      minWidth: 0,
    },
  stockSearchActions: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      flex: '0 0 auto',
    },
  stockPrimaryButton: {
      height: 44,
      padding: '0 16px',
      border: 0,
      borderRadius: 7,
      background: 'var(--pi-ink)',
      color: '#fff',
      fontSize: 12,
      fontWeight: 750,
      cursor: 'pointer',
    },
  stockGhostButton: {
      height: 44,
      padding: '0 13px',
      border: `1px solid ${UI.borderStrong}`,
      borderRadius: 8,
      background: '#fff',
      color: UI.textSub,
      fontSize: 12,
      fontWeight: 850,
      cursor: 'pointer',
    },
  stockGhostButtonActive: {
      background: '#eef4f8',
      color: UI.blue,
      borderColor: '#bfd0dd',
    },
  stockFilterPanel: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))',
      gap: 8,
      padding: 10,
      marginBottom: 8,
      border: `1px solid ${UI.border}`,
      borderRadius: 10,
      background: '#fff',
    },
  stockFilterField: {
      display: 'grid',
      gap: 5,
      color: UI.textMuted,
      fontSize: 9.5,
      fontWeight: 850,
    },
  stockFilterInput: {
      width: '100%',
      height: 36,
      boxSizing: 'border-box',
      border: `1px solid ${UI.border}`,
      borderRadius: 8,
      background: '#fff',
      color: UI.text,
      padding: '0 9px',
      fontSize: 11,
      fontWeight: 800,
    },
  stockFilterReset: {
      minHeight: 36,
      border: `1px solid ${UI.border}`,
      borderRadius: 8,
      background: '#f8fafc',
      color: UI.textSub,
      fontWeight: 850,
    },
  stockError: {
      marginBottom: 8,
      padding: '10px 12px',
      borderRadius: 9,
      background: UI.redSoft,
      color: UI.red,
      fontSize: 11.5,
      fontWeight: 800,
    },
  stockCandidateBar: {
      marginBottom: 10,
      border: `1px solid ${UI.border}`,
      borderRadius: 7,
      background: '#fff',
      overflow: 'hidden',
    },
  stockCandidateHead: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 10,
      padding: '9px 11px 7px',
    },
  stockCandidateTitle: {
      display: 'flex',
      alignItems: 'baseline',
      gap: 8,
      minWidth: 0,
      color: UI.text,
      fontSize: 11.5,
      fontWeight: 750,
    },
  stockCandidateActions: {
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      flex: '0 0 auto',
    },
  stockPagerButton: {
      width: 28,
      height: 28,
      border: `1px solid ${UI.border}`,
      borderRadius: 7,
      background: '#fff',
      color: UI.textSub,
      fontSize: 16,
      fontWeight: 750,
      cursor: 'pointer',
    },
  stockTextButton: {
      border: 0,
      background: 'transparent',
      color: UI.blue,
      fontSize: 10.5,
      fontWeight: 750,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      padding: '5px 4px',
    },
  stockDetailCard: {
      minWidth: 0,
      overflow: 'hidden',
      border: `1px solid ${UI.border}`,
      borderRadius: 7,
      background: '#fff',
      boxShadow: 'none',
    },
  stockQuoteHeader: {
      display: 'grid',
      gridTemplateColumns: 'minmax(0,1fr) minmax(360px,.72fr)',
      alignItems: 'end',
      gap: 24,
      padding: '18px 20px 15px',
    },
  stockQuoteHeaderMobile: {
      gridTemplateColumns: '1fr',
      alignItems: 'stretch',
      gap: 14,
      padding: '15px 14px 12px',
    },
  stockQuoteMain: {
      minWidth: 0,
    },
  stockIdentityRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      flexWrap: 'wrap',
      minWidth: 0,
    },
  stockName: {
      margin: 0,
      color: UI.text,
      fontSize: 21,
      lineHeight: 1.2,
      fontWeight: 800,
      letterSpacing: '-.025em',
    },
  stockTicker: {
      color: UI.textMuted,
      fontSize: 10.5,
      fontWeight: 800,
    },
  stockPriceRow: {
      display: 'flex',
      alignItems: 'baseline',
      gap: 10,
      marginTop: 8,
    },
  stockPrice: {
      color: UI.text,
      fontSize: 30,
      lineHeight: 1,
      fontWeight: 800,
      letterSpacing: '-.035em',
    },
  stockChange: {
      fontSize: 13,
      fontWeight: 750,
    },
  stockQuoteMetrics: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4,minmax(0,1fr))',
      minWidth: 0,
      border: `1px solid ${UI.border}`,
      borderRadius: 10,
      overflow: 'hidden',
    },
  stockQuoteMetric: {
      minWidth: 0,
      display: 'grid',
      gap: 4,
      padding: '10px 9px',
      borderRight: `1px solid ${UI.border}`,
      color: UI.textMuted,
      fontSize: 9.2,
      fontWeight: 800,
    },
  stockTabs: {
      display: 'flex',
      alignItems: 'center',
      gap: 0,
      overflowX: 'auto',
      scrollbarWidth: 'none',
      borderTop: `1px solid ${UI.border}`,
      borderBottom: `1px solid ${UI.border}`,
      background: '#fff',
    },
  stockTabButton: {
      flex: '0 0 auto',
      minHeight: 46,
      padding: '0 17px',
      // Do not mix border/borderBottom shorthands with borderBottomColor in the active style.
      borderTopWidth: 0,
      borderRightWidth: 0,
      borderBottomWidth: 2,
      borderLeftWidth: 0,
      borderTopStyle: 'solid',
      borderRightStyle: 'solid',
      borderBottomStyle: 'solid',
      borderLeftStyle: 'solid',
      borderTopColor: 'transparent',
      borderRightColor: 'transparent',
      borderBottomColor: 'transparent',
      borderLeftColor: 'transparent',
      background: 'transparent',
      color: UI.textMuted,
      fontSize: 11.5,
      fontWeight: 850,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
    },
  stockTabButtonActive: {
      borderBottomColor: UI.blue,
      color: UI.text,
      fontWeight: 800,
    },
  stockTabContent: {
      minWidth: 0,
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden',
      background: '#fff',
    },
  stockOverviewHero: {
      display: 'grid',
      gap: 16,
      padding: '18px 20px',
      borderBottom: `1px solid ${UI.border}`,
      background: '#fff',
      minWidth: 0,
    },
  stockOverviewHeroMain: {
      minWidth: 0,
    },
  stockOverviewEyebrow: {
      color: UI.blue,
      fontSize: 9,
      lineHeight: 1.2,
      fontWeight: 900,
      letterSpacing: '.10em',
    },
  stockOverviewHeadlineRow: {
      display: 'flex',
      alignItems: 'baseline',
      gap: 10,
      flexWrap: 'wrap',
      marginTop: 5,
    },
  stockOverviewHeadline: {
      fontSize: 23,
      lineHeight: 1.2,
      fontWeight: 900,
      letterSpacing: '-.03em',
    },
  stockOverviewScore: {
      color: UI.textMuted,
      fontSize: 11,
      fontWeight: 850,
    },
  stockMetaLabel: {
      color: UI.textMuted,
      fontSize: 9.5,
      lineHeight: 1.3,
      fontWeight: 850,
    },
  stockMetaValue: {
      color: UI.text,
      fontSize: 14,
      lineHeight: 1.3,
      fontWeight: 900,
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  stockOverviewSummary: {
      margin: '7px 0 0',
      color: UI.textSub,
      fontSize: 12.5,
      lineHeight: 1.65,
      fontWeight: 650,
      wordBreak: 'keep-all',
    },
  stockConditionGrid: {
      display: 'grid',
      gap: 8,
      minWidth: 0,
    },
  stockConditionCard: {
      minWidth: 0,
      padding: '11px 12px',
      border: `1px solid ${UI.border}`,
      borderRadius: 10,
      background: '#fbfcfd',
      display: 'grid',
      gap: 4,
    },
  stockOverviewSection: {
      padding: '16px 20px',
      borderBottom: `1px solid ${UI.border}`,
      minWidth: 0,
      background: '#fff',
    },
  stockOverviewSectionHead: {
      display: 'flex',
      alignItems: 'end',
      justifyContent: 'space-between',
      gap: 12,
      marginBottom: 12,
      minWidth: 0,
    },
  stockOverviewSectionTitle: {
      margin: '3px 0 0',
      color: UI.text,
      fontSize: 18,
      lineHeight: 1.25,
      fontWeight: 900,
      letterSpacing: '-.025em',
    },
  stockOverviewSectionHint: {
      color: UI.textMuted,
      fontSize: 10.5,
      lineHeight: 1.4,
      textAlign: 'right',
    },
  stockOverviewMetricGrid: {
      display: 'grid',
      gap: 0,
      border: `1px solid ${UI.border}`,
      borderRadius: 10,
      overflow: 'hidden',
      minWidth: 0,
    },
  stockOverviewMetric: {
      minWidth: 0,
      padding: '11px 12px',
      borderRight: `1px solid ${UI.border}`,
      background: '#fff',
      display: 'grid',
      gap: 4,
    },
  stockOverviewColumns: {
      display: 'grid',
      gap: 9,
      minWidth: 0,
    },
  stockOverviewLinkCard: {
      minWidth: 0,
      width: '100%',
      minHeight: 104,
      padding: '13px 14px',
      border: `1px solid ${UI.border}`,
      borderRadius: 11,
      background: '#fff',
      textAlign: 'left',
      cursor: 'pointer',
      display: 'grid',
      alignContent: 'start',
      gap: 6,
      color: UI.text,
    },
  stockOverviewLinkLabel: {
      color: UI.blue,
      fontSize: 9.5,
      lineHeight: 1.2,
      fontWeight: 900,
      letterSpacing: '.05em',
    },
  stockOverviewLinkTitle: {
      color: UI.text,
      fontSize: 13.5,
      lineHeight: 1.4,
      fontWeight: 900,
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  stockOverviewLinkText: {
      color: UI.textMuted,
      fontSize: 10.2,
      lineHeight: 1.5,
      fontWeight: 650,
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    },
  stockTabIntro: {
      display: 'flex',
      alignItems: 'end',
      justifyContent: 'space-between',
      gap: 16,
      padding: '15px 20px 12px',
      borderBottom: `1px solid ${UI.border}`,
      background: '#fff',
      minWidth: 0,
    },
  stockTabTitle: {
      margin: '3px 0 0',
      color: UI.text,
      fontSize: 18,
      fontWeight: 900,
      letterSpacing: '-.025em',
    },
  stockTabIntroText: {
      margin: 0,
      maxWidth: 680,
      color: UI.textMuted,
      fontSize: 10.8,
      lineHeight: 1.55,
      textAlign: 'right',
      wordBreak: 'keep-all',
    },
  stockTechnicalGrid: {
      display: 'grid',
      gap: 0,
      minWidth: 0,
      borderBottom: `1px solid ${UI.border}`,
      background: '#fff',
    },
  stockTechnicalMetric: {
      minWidth: 0,
      padding: '12px 14px',
      borderRight: `1px solid ${UI.border}`,
      display: 'grid',
      gap: 4,
    },
  stockTechnicalNote: {
      padding: '11px 16px',
      background: '#f8fafc',
      color: UI.textMuted,
      fontSize: 10.5,
      lineHeight: 1.55,
      borderTop: `1px solid ${UI.border}`,
    },
  stockFlowPanel: {
      minWidth: 0,
    },
  stockFlowSummary: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3,minmax(0,1fr))',
      gap: 0,
      borderBottom: `1px solid ${UI.border}`,
      background: '#fbfcfd',
    },
  stockFlowMetric: {
      minWidth: 0,
      padding: '10px 12px',
      borderRight: `1px solid ${UI.border}`,
      color: UI.text,
      fontSize: 11.5,
      fontWeight: 750,
    },
  stockFlowLabel: {
      display: 'block',
      marginBottom: 3,
      color: UI.textMuted,
      fontSize: 9.2,
      fontWeight: 800,
    },
  stockIssuePanel: {
      padding: 16,
      background: '#fff',
    },
  stockIssueHead: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 12,
      marginBottom: 12,
    },
  stockIssueEyebrow: {
      color: UI.blue,
      fontSize: 8.8,
      fontWeight: 800,
      letterSpacing: '.1em',
    },
  stockIssueTitle: {
      margin: '3px 0 0',
      color: UI.text,
      fontSize: 16,
      fontWeight: 800,
    },
  stockIssueSub: {
      margin: '4px 0 0',
      color: UI.textMuted,
      fontSize: 10.2,
      lineHeight: 1.45,
    },
  stockIssueList: {
      display: 'grid',
      borderTop: `1px solid ${UI.border}`,
    },
  stockIssueItem: {
      display: 'grid',
      gridTemplateColumns: '100px minmax(0,1fr)',
      gap: 14,
      padding: '12px 2px',
      borderBottom: `1px solid ${UI.border}`,
    },
  stockIssueLabel: {
      color: UI.textMuted,
      fontSize: 10,
      fontWeight: 750,
    },
  stockIssueText: {
      color: UI.textSub,
      fontSize: 11.5,
      lineHeight: 1.6,
      fontWeight: 700,
    },
  stockIssueEmpty: {
      padding: 24,
      border: `1px dashed ${UI.borderStrong}`,
      borderRadius: 10,
      color: UI.textMuted,
      fontSize: 11,
      textAlign: 'center',
      background: '#fbfcfd',
    },
  stockEmptyState: {
      minHeight: 520,
      display: 'grid',
      placeItems: 'center',
      alignContent: 'center',
      gap: 7,
      border: `1px solid ${UI.border}`,
      borderRadius: 14,
      background: '#fff',
      color: UI.textMuted,
    },
  stockTableDrawer: {
      marginTop: 10,
      border: `1px solid ${UI.border}`,
      borderRadius: 7,
      background: '#fff',
      overflow: 'hidden',
    },
  stockTableHead: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 10,
      padding: '11px 12px',
      borderBottom: `1px solid ${UI.border}`,
    },
  stockFooterHint: {
      padding: '10px 3px 2px',
      color: UI.textMuted,
      fontSize: 10.2,
      lineHeight: 1.5,
    },
  metricCard: {
      background: UI.card,
      border: `1px solid ${UI.border}`,
      borderRadius: 16,
      padding: 15,
      boxShadow: '0 1px 5px rgba(15,23,42,.045)',
      minWidth: 0,
    },
  metricLabel: {
      fontSize: 12,
      color: UI.textMuted,
      fontWeight: 850,
      marginBottom: 8,
    },
  metricValue: {
      fontSize: 24,
      fontWeight: 800,
      letterSpacing: '-0.03em',
    },
  metricDesc: {
      marginTop: 4,
      fontSize: 12,
      color: UI.textSub,
    },
  sidePanel: {
      position: 'sticky',
      top: 14,
      background: UI.card,
      border: `1px solid ${UI.border}`,
      borderRadius: 12,
      padding: 16,
      boxShadow: '0 2px 8px rgba(15,23,42,.06)',
      minWidth: 0,
    },
  sidePanelMobile: {
      position: 'static',
      top: 'auto',
      borderRadius: 14,
      padding: 12,
    },
  sideHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: 12,
      alignItems: 'flex-start',
      marginBottom: 14,
    },
  sideEyebrow: {
      color: UI.blue,
      fontSize: 11,
      fontWeight: 800,
      letterSpacing: 0.6,
      textTransform: 'uppercase',
    },
  sideTitle: {
      margin: '4px 0 0',
      fontSize: 20,
      lineHeight: 1.25,
      fontWeight: 800,
    },
  sideNotice: {
      border: `1px solid ${UI.border}`,
      background: UI.cardSoft,
      borderRadius: 12,
      padding: 10,
      marginBottom: 12,
      fontSize: 12,
      color: UI.textSub,
    },
  conclusionBox: {
      border: `1px solid ${UI.border}`,
      background: UI.cardSoft,
      borderRadius: 14,
      padding: 13,
      marginBottom: 12,
    },
  boxTitle: {
      fontSize: 12,
      fontWeight: 800,
      color: UI.text,
      marginBottom: 7,
    },
  boxText: {
      fontSize: 13,
      lineHeight: 1.65,
      color: UI.textSub,
    },
  sideMetricGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: 8,
      marginBottom: 12,
    },
  scoreBox: {
      display: 'grid',
      gap: 9,
      border: `1px solid ${UI.border}`,
      background: UI.cardSoft,
      borderRadius: 14,
      padding: 13,
      marginBottom: 12,
    },
  scoreHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: 12,
      color: UI.textSub,
      fontWeight: 850,
      marginBottom: 5,
    },
  scoreTrack: {
      width: '100%',
      height: 7,
      borderRadius: 999,
      background: '#e7edf4',
      overflow: 'hidden',
    },
  scoreFill: {
      height: '100%',
      borderRadius: 999,
    },
  reasonBox: {
      border: `1px solid ${UI.border}`,
      background: UI.cardSoft,
      borderRadius: 14,
      padding: 13,
      marginBottom: 12,
    },
  chipWrap: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 6,
    },
  financialLine: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      flexWrap: 'wrap',
      color: UI.textSub,
      fontSize: 12,
    },
  financialText: {
      margin: '10px 0 0',
      color: UI.textSub,
      fontSize: 13,
      lineHeight: 1.65,
    },
  checkList: {
      margin: 0,
      paddingLeft: 18,
      color: UI.textSub,
      fontSize: 13,
      lineHeight: 1.75,
    },
  emptySide: {
      minHeight: 340,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      textAlign: 'center',
      padding: 18,
    },
  emptyIcon: {
      width: 54,
      height: 54,
      borderRadius: 12,
      border: `1px solid ${UI.border}`,
      background: UI.cardSoft,
      color: UI.blue,
      display: 'grid',
      placeItems: 'center',
      fontSize: 26,
      fontWeight: 750,
      marginBottom: 12,
    },
  emptyTitle: {
      fontSize: 18,
      fontWeight: 800,
      color: UI.text,
    },
  emptyText: {
      marginTop: 8,
      color: UI.textSub,
      fontSize: 13,
      lineHeight: 1.65,
      maxWidth: 300,
    },
  businessHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: 12,
      marginBottom: 12,
    },
  businessSubTitle: {
      marginTop: 5,
      color: UI.textMuted,
      fontSize: 12,
      lineHeight: 1.5,
    },
  businessSummaryCard: {
      padding: '13px 14px',
      border: `1px solid ${UI.border}`,
      borderRadius: 12,
      background: UI.cardSoft,
    },
  businessSummaryCardMissing: {
      borderStyle: 'dashed',
      background: '#fbfcfe',
    },
  businessSectionLabel: {
      color: UI.textMuted,
      fontSize: 10.5,
      fontWeight: 750,
      letterSpacing: '0.01em',
    },
  businessSummaryText: {
      margin: '6px 0 0',
      color: UI.text,
      fontSize: 12.5,
      fontWeight: 650,
      lineHeight: 1.72,
      wordBreak: 'keep-all',
    },
  businessSummaryTextMissing: {
      color: UI.textMuted,
      fontWeight: 700,
    },
  businessMetaGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: 8,
      marginTop: 9,
    },
  businessMetaItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      minWidth: 0,
      padding: '10px 11px',
      border: `1px solid ${UI.border}`,
      borderRadius: 10,
      background: UI.card,
      color: UI.textMuted,
      fontSize: 10.5,
    },
  businessChipSection: {
      marginTop: 11,
    },
  businessChipRow: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 6,
      marginTop: 7,
    },
  businessChip: {
      display: 'inline-flex',
      alignItems: 'center',
      maxWidth: '100%',
      padding: '5px 8px',
      borderRadius: 999,
      background: UI.blueSoft,
      color: UI.blue,
      fontSize: 10.5,
      fontWeight: 850,
      lineHeight: 1.25,
    },
  businessChipTech: {
      background: UI.greenSoft,
      color: UI.green,
    },
  businessChipMuted: {
      background: UI.slateSoft,
      color: UI.slate,
    },
  businessReason: {
      marginTop: 10,
      padding: '9px 11px',
      borderRadius: 10,
      background: UI.blueSoft,
      color: UI.textSub,
      fontSize: 11.5,
      lineHeight: 1.55,
    },
  businessEmpty: {
      padding: 12,
      border: `1px dashed ${UI.borderStrong}`,
      borderRadius: 12,
      background: UI.cardSoft,
      color: UI.textMuted,
      fontSize: 12.5,
      lineHeight: 1.6,
    },
  navigatorList: {
      display: 'grid',
      gap: 6,
      padding: 7,
      overflowY: 'auto',
      minHeight: 0,
      scrollbarWidth: 'thin',
    },
  navigatorEmpty: {
      padding: 18,
      color: UI.textMuted,
      fontSize: 11,
      textAlign: 'center',
    },
  insightPanel: {
      position: 'sticky',
      top: 112,
      alignSelf: 'start',
      minWidth: 0,
      maxHeight: 'calc(100vh - 92px)',
      overflowY: 'auto',
      background: '#fff',
      border: `1px solid ${UI.border}`,
      borderRadius: 10,
      padding: 11,
      boxShadow: '0 2px 10px rgba(15,23,42,.045)',
    },
  insightPanelCompact: {
      position: 'static',
      top: 'auto',
      maxHeight: 'none',
      padding: 10,
      borderRadius: 10,
    },
  insightEmpty: {
      minHeight: 110,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      gap: 6,
      color: UI.textMuted,
      fontSize: 11,
      lineHeight: 1.5,
      padding: 14,
    },
  insightHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: 8,
      paddingBottom: 9,
      borderBottom: `1px solid ${UI.border}`,
    },
  insightEyebrow: {
      color: UI.blue,
      fontSize: 8.8,
      fontWeight: 800,
      letterSpacing: '.1em',
    },
  insightName: {
      color: UI.text,
      fontSize: 16,
      fontWeight: 800,
      marginTop: 3,
      lineHeight: 1.25,
    },
  insightTicker: {
      color: UI.textMuted,
      fontSize: 9.5,
      fontWeight: 850,
      marginTop: 2,
    },
  insightNotice: {
      marginTop: 8,
      padding: '7px 8px',
      borderRadius: 8,
      background: UI.cardSoft,
      color: UI.textSub,
      fontSize: 10,
    },
  insightDecisionSection: {
      marginTop: 9,
      padding: '9px 0 8px',
      borderBottom: `1px solid ${UI.border}`,
    },
  insightDecisionHead: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
      color: UI.textMuted,
      fontSize: 9.5,
      fontWeight: 750,
    },
  insightDecisionText: {
      marginTop: 5,
      color: UI.text,
      fontSize: 11.2,
      lineHeight: 1.5,
      fontWeight: 850,
    },
  insightScoreStrip: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      marginTop: 6,
      color: UI.textMuted,
      fontSize: 9.3,
      fontWeight: 850,
    },
  insightSectionTitle: {
      color: UI.text,
      fontSize: 10.5,
      fontWeight: 800,
      marginBottom: 6,
    },
  insightEvidenceGrid: {
      display: 'grid',
      gap: 8,
      marginTop: 10,
    },
  insightEvidenceBlock: {
      minWidth: 0,
      padding: '8px 9px',
      border: `1px solid ${UI.border}`,
      borderRadius: 8,
      background: UI.cardSoft,
    },
  insightEvidenceList: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
      display: 'grid',
      gap: 6,
    },
  insightEvidenceItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 6,
      color: UI.textSub,
      fontSize: 9.8,
      lineHeight: 1.45,
      fontWeight: 800,
    },
  insightEvidenceDot: {
      width: 5,
      height: 5,
      borderRadius: 999,
      marginTop: 4,
      flex: '0 0 auto',
    },
  mobileTickerRail: {
      display: 'flex',
      gap: 6,
      overflowX: 'auto',
      padding: '7px 8px',
      minWidth: 0,
      scrollbarWidth: 'none',
      scrollSnapType: 'x proximity',
    },
  businessFinancialPanel: {
      marginTop: 10,
      background: '#fff',
      border: `1px solid ${UI.border}`,
      borderRadius: 10,
      padding: 12,
      boxShadow: '0 2px 10px rgba(15,23,42,.045)',
    },
  businessFinancialPanelMobile: {
      marginTop: 0,
      padding: 10,
    },
  businessFinancialHeader: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 10,
      flexWrap: 'wrap',
      marginBottom: 10,
    },
  businessFinancialEyebrow: {
      color: UI.blue,
      fontSize: 8.8,
      fontWeight: 800,
      letterSpacing: '.1em',
    },
  businessFinancialTitle: {
      margin: '2px 0 0',
      color: UI.text,
      fontSize: 16,
      lineHeight: 1.25,
      fontWeight: 800,
    },
  businessFinancialSub: {
      margin: '4px 0 0',
      color: UI.textMuted,
      fontSize: 10.2,
      lineHeight: 1.45,
    },
  businessFinancialHeaderBadges: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      flexWrap: 'wrap',
    },
  businessFinancialRisk: {
      color: UI.textSub,
      fontSize: 9.5,
      fontWeight: 850,
    },
  businessFinancialGrid: {
      display: 'grid',
      gap: 10,
      alignItems: 'start',
      minWidth: 0,
    },
  financialSummaryPanel: {
      border: `1px solid ${UI.border}`,
      background: UI.cardSoft,
      borderRadius: 12,
      padding: 12,
      minWidth: 0,
    },
  financialSummaryHead: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: 8,
      alignItems: 'flex-start',
    },
  financialMetricGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2,minmax(0,1fr))',
      gap: 6,
      marginTop: 10,
    },
  financialMetricItem: {
      minWidth: 0,
      padding: '8px 9px',
      border: `1px solid ${UI.border}`,
      borderRadius: 9,
      background: '#fff',
      display: 'grid',
      gap: 3,
      color: UI.textMuted,
      fontSize: 9.3,
      fontWeight: 850,
    }
};

export default StockCategoryPage;
